import jsPDF from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import moment from 'moment';
import { GeneratePDFService } from '../services/GeneratePDFService';
import { LocalStorageService } from '../services/LocalStorageService';
import { logger } from '../utilities/logger';
import logoBase64 from '../assests/images/logobase64';
import backingyesBase64 from '../assests/images/backingyesbase64';
import backingnoBase64 from '../assests/images/backingnobase64';
import drillingyesBase64 from '../assests/images/drillingyesbase64';
import drillingnoBase64 from '../assests/images/drillingnobase64'
/**
 * usePDFGenerator
 * Custom hook that encapsulates all PDF generation logic previously
 * housed in handleSubmit inside Index.js.
 *
 * @param {object} params - All state values and setters needed by the PDF generator.
 */
export function usePDFGenerator({
  // ── form data ──────────────────────────────────
  jobID,
  designerName,
  clientName,
  clientemail,
  closetsFormCount,
  billingProvince,
  templateId,
  pdfEventType,
  pdfURL,
  designFileURL,
  pricePresentationFileURL,
  kitchenSpacesEnabled,
  statusCard,
  statusCardNumber,
  // ── per-type financial summary blocks (see useFinancialSummary) ──
  // statusCard/statusCardNumber above are intentionally shared across both
  // blocks — there is one status-card yes/no per job, not per closet/kitchen.
  closetFinance,
  kitchenFinance,
  hasClosetForms,
  hasKitchenForms,

  // ── pre-computed display arrays ─────────────────
  clientDetails,
  billingAddressFields,
  extendedClientAddressFields,
  // ── state setters ───────────────────────────────
  setTouched,
  setShowLoading,
  setHasSeenPreview,
  setHasChangesAfterSend,
  // ── notification ────────────────────────────────
  notify,
}) {

  const base64ToUint8Array = (base64) => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    let str = base64.replace(/=+$/, ""); // remove padding
    let bytes = [];

    for (let i = 0, j = 0; i < str.length; i += 4) {
      const enc1 = chars.indexOf(str[i]);
      const enc2 = chars.indexOf(str[i + 1]);
      const enc3 = chars.indexOf(str[i + 2]);
      const enc4 = chars.indexOf(str[i + 3]);

      const triplet =
        (enc1 << 18) | (enc2 << 12) | ((enc3 & 63) << 6) | (enc4 & 63);

      if (enc3 === -1) {
        bytes[j++] = (triplet >> 16) & 255;
      } else if (enc4 === -1) {
        bytes[j++] = (triplet >> 16) & 255;
        bytes[j++] = (triplet >> 8) & 255;
      } else {
        bytes[j++] = (triplet >> 16) & 255;
        bytes[j++] = (triplet >> 8) & 255;
        bytes[j++] = triplet & 255;
      }
    }

    return new Uint8Array(bytes);
  };

  const blobToUint8Array = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  };

  const handleSubmit = async (isSavePreviewOnly = false) => {
    //setShowLoading("block");
    try {
      // Mark all fields as touched
      setTouched({
        jobID: true,
        clientName: true,
        designerName: true,
        clienttelephone: true,
        clientemail: true,
        closetsFormCount: true,
        billingAddress: true,
        billingCity: true,
        billingProvince,
        billingPostalCode: true,
        city: true,
        province: true,
        postalCode: true,
        statusCard: true,
        statusCardNumber: true,
        subTotal: true,
        total: true,
        hst: true,
        deposit: true,
        balanceDue: true,
        paymentMethod: true,
        chequeNumber: true,
        //cardNumber: true,
        //cardHolderName: true,
        //expDate: true,
        //cvv: true,
      });

      const doc = new jsPDF("p", "mm", "a4");

      //getting the child object
      // const resultArray = await GeneratePDFService.GetChildContent(jobID);
      const allChildren = await GeneratePDFService.GetChildContent(jobID);
      const resultArray = allChildren.filter((item) => {
        const extractedJobId = String(item.jobid).split("-")[0];
        return extractedJobId === String(jobID);
      });

      // Parse once, reuse everywhere below.
      const parsedEntries = resultArray.map((item) => JSON.parse(item.jsoncontent));

      // Group by type, preserving original order within each group.
      const closetIndices = [];
      const kitchenIndices = [];
      parsedEntries.forEach((jsonContent, idx) => {
        if (jsonContent.kitchenSpacesEnabled === true) {
          kitchenIndices.push(idx);
        } else {
          closetIndices.push(idx);
        }
      });


      function drawHeader(doc, logoBase64, jobID, showJobSite = true) {
        // Add the image to the PDF
        doc.addImage(logoBase64, "PNG", 3, 18.5, 60, 9); // Adjust x, y, width, height as needed

        // ----------------------
        // Company Name (auto-fit inside logo area)
        // ----------------------

        const boxX = 10;
        const boxY = 8;
        const boxW = 50;
        const boxH = 25;

        //const companyName = "2340993 Ontario Limited";

        doc.setFont("helvetica", "bold");

        // Start with a large font and shrink if needed
        let fontSize = 18;
        doc.setFontSize(fontSize);

        // Reduce font size until it fits within width
        /* while (doc.getTextWidth(companyName) > boxW && fontSize > 8) {
           fontSize -= 0.5;
           doc.setFontSize(fontSize);
         }

        // Center horizontally inside the box
        const textWidth = doc.getTextWidth(companyName);
        const textX = boxX + (boxW - textWidth) / 2;

        // Center vertically inside the box
        const textY = boxY + (boxH / 2) + (fontSize / 3);

        doc.text(companyName, textX, textY);*/

        // ----------------------
        // 1. Compact Header Table (Job ID, Designer, Date)
        // ----------------------

        // Define a narrow header table with width = 60 mm. Place it in the top-right corner.
        const pageWidth = 210; // A4 width in mm (approx.)
        const headerTableW = 70; // Make the width of the table smaller
        const headerTableH = 17; // Increase the height of the table
        const headerTableX = pageWidth - headerTableW - 3; // 5mm right margin
        const headerTableY = 15;
        doc.setLineWidth(0.1);
        doc.rect(headerTableX, headerTableY, headerTableW, headerTableH);

        // Address lines
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        const addressLines = [
          "73 Saunders Road, Barrie, Ontario, L4N 9A7",
          "Tel (705) 315-9169 • Fax (705) 727-1102",
          "www.dreamspaces.ca • (800) 293-3744",
          "CANADIAN OWNED AND OPERATED",
        ];

        // Adjust the starting x and Y positions for the address
        const addressX = headerTableX - 58; // Move more to the left (5mm further left)
        const addressY = headerTableY + 4; // Starting Y position

        addressLines.forEach((line, i) => {
          doc.text(line, addressX, addressY + i * 4);
          // Reduce gap between lines by changing the multiplier to 4
        });

        // Divide the table into 3 equal rows
        const headerRowH = headerTableH / 3;

        // Draw horizontal lines
        doc.line(
          headerTableX,
          headerTableY + headerRowH,
          headerTableX + headerTableW,
          headerTableY + headerRowH
        );
        doc.line(
          headerTableX,
          headerTableY + 2 * headerRowH,
          headerTableX + headerTableW,
          headerTableY + 2 * headerRowH
        );
        // Draw vertical line (split in half)
        doc.line(
          headerTableX + headerTableW / 3,
          headerTableY,
          headerTableX + headerTableW / 3,
          headerTableY + headerTableH
        );

        // Header row labels (centered, using bold)
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(
          "Job ID",
          headerTableX + 10,
          headerTableY + headerRowH / 2 + 1,
          { align: "center" }
        );
        doc.text(
          "Designer",
          headerTableX + 10,
          headerTableY + headerRowH + headerRowH / 2 + 1,
          { align: "center" }
        );
        doc.text(
          "Date",
          headerTableX + 10,
          headerTableY + 2 * headerRowH + headerRowH / 2 + 1,
          { align: "center" }
        );


        // Data row: retrieve actual field values.
        const jobId = jobID; // your field value
        const designerNames = designerName; // your field value
        const today = new Date();
        const day = today.getDate().toString().padStart(2, "0");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[today.getMonth()];
        const year = today.getFullYear().toString().slice(-2);
        const formattedDate = `${day}-${month}-${year}`;

        doc.setFont("helvetica", "normal");
        doc.text(
          jobId,
          headerTableX + headerTableW / 2 + 4,
          headerTableY + headerRowH / 2 + 1,
          { align: "center" }
        );
        doc.text(
          designerNames,
          headerTableX + headerTableW / 2 + 4,
          headerTableY + headerRowH + headerRowH / 2 + 1,
          { align: "center" }
        );
        doc.text(
          formattedDate,
          headerTableX + headerTableW / 2 + 4,
          headerTableY + 2 * headerRowH + headerRowH / 2 + 1,
          { align: "center" }
        );
        logger.debug("Designer Name in PDF:", designerNames);


        // ----------------------
        // 2. Client Details Tables (Basic & Address) – Rendered as Grid-like Tables
        // ----------------------

        // Assume these arrays are already defined:
        // clientDetails = [ { label: "Name", value: "Alice Smith" }, { label: "Phone", value: "555-1234" }, ... ]
        // clientAddressFields = [ { label: "Street", value: "123 Main St" }, { label: "City", value: "Toronto" }, ... ]

        // Use a narrow margin (5 mm) all around.
        const marginX = 3;
        const startY = headerTableY + headerTableH + 8;

        // Each table will have width = 90 mm and a height to fit the fields.
        const tableW = 204;
        const tableH = 17; // Adjust as needed.
        //const gapBetween = 2; // gap between the two tables

        // -- Client Basic Details Table --
        const clientBasicX = marginX;
        const clientBasicY = startY;
        doc.rect(clientBasicX, clientBasicY, tableW, tableH);
        doc.setFont("helvetica", "bold");
        doc.text("Client Basic Details", clientBasicX + 2, clientBasicY + 5);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        // Increase the gap between header and fields by adding 5mm to clientBasicY
        const fieldStartY = clientBasicY + 8 + 1; // Add 5mm gap between header and fields

        // Render the fields in a grid structure. Let’s use two columns (each field as a row with label on left, value on right).
        const basicFieldCols = 3;
        const basicRowHeight = 6;
        //const basicLabelW = 30;  // Label column width
        const basicRows = Math.ceil(clientDetails.length / basicFieldCols);

        // Filter out Job ID and Designer Name from clientDetails array
        const filteredClientDetails = clientDetails.filter(
          (field) => field.label !== "Job ID" && field.label !== "Designer Name"
        );

        // Now use filteredClientDetails for rendering the table
        for (let col = 0; col < basicFieldCols; col++) {
          for (let row = 0; row < basicRows; row++) {
            const index = col * basicRows + row;
            if (index >= filteredClientDetails.length) break;
            const field = filteredClientDetails[index];
            const x = clientBasicX + 2 + col * (tableW / basicFieldCols);
            const y = fieldStartY + row * basicRowHeight; // Use fieldStartY for the Y position

            const labelWidth = doc.getTextWidth(field.label + ":");
            const detailsX = x + labelWidth + 3;

            // Draw the field as "Label: Value"
            doc.setFont("helvetica", "bold");
            doc.text(field.label + ":", x, y);
            doc.setFont("helvetica", "normal");
            doc.text(String(field.value), detailsX, y);
          }
        }

        // // -- Address/Location Details Table --
        // const clientAddrX = clientBasicX;
        // const clientAddrY = startY + 36; // Starting Y position for the table

        // Billing Address Details Table
        const billingAddrX = marginX;
        const billingAddrY = 22 + tableH + 19;
        doc.rect(billingAddrX, billingAddrY, tableW, tableH);

        // Title
        doc.setFont("helvetica", "bold");
        doc.text("Billing Address Details", billingAddrX + 2, billingAddrY + 5);

        // Font for fields
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        const billingFieldCols = 2;
        const billingRowHeight = 6;
        const billingTitleGap = 10; // ⬅️ increase this to create more space
        const billingFieldStartY = billingAddrY + billingTitleGap;

        const billingRows = Math.ceil(
          billingAddressFields.length / billingFieldCols
        );

        for (let col = 0; col < billingFieldCols; col++) {
          for (let row = 0; row < billingRows; row++) {
            const index = col * billingRows + row;
            if (index >= billingAddressFields.length) break;

            const field = billingAddressFields[index];
            const x = billingAddrX + 2 + col * (tableW / billingFieldCols);
            const y = billingFieldStartY + row * billingRowHeight;

            const labelWidth = doc.getTextWidth(field.label + ":");
            const valueX = x + labelWidth + 3;

            doc.setFont("helvetica", "bold");
            doc.text(field.label + ":", x, y);
            doc.setFont("helvetica", "normal");
            doc.text(field.value, valueX, y);
          }
        }

        // Job Sight Address Details Table
        // Increase the gap between header and fields by adding 5mm to clientAddrY
        if (showJobSite) {
        }
      }

      //
      // ----------------------
      // 3. Closet Details Section (2-Column Grid with Aligned Cells)
      // ----------------------

      drawHeader(doc, logoBase64, jobID, true);

      const pageWidth = doc.internal.pageSize.getWidth();
      if (closetIndices.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text("ORDER CONFIRMATION", pageWidth / 2, 12, { align: "center" });
      }

      function drawJobSiteAddress(
        doc,
        jsonContent,
        extendedClientAddressFields,
        startY = 76
      ) {
        const clientAddrX = 3;
        const tableW = 204;
        const clientAddrY = startY;
        const addrFieldStartY = clientAddrY + 8 + 1;

        doc.rect(clientAddrX, clientAddrY, tableW, 22);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Job Site Address", clientAddrX + 2, clientAddrY + 5);

        const addrFieldCols = 3;
        const addrRowHeight = 6;
        const addrRows = Math.ceil(
          extendedClientAddressFields.length / addrFieldCols
        );

        for (let col = 0; col < addrFieldCols; col++) {
          for (let row = 0; row < addrRows; row++) {
            const index = col * addrRows + row;
            if (index >= extendedClientAddressFields.length) break;
            const field = extendedClientAddressFields[index];

            let x = clientAddrX + 2 + col * (tableW / addrFieldCols);
            if (col === 1) x += 25;
            if (col === 2) x += 15;

            const y = addrFieldStartY + row * addrRowHeight;
            const labelWidth = doc.getTextWidth(field.label + ":");
            const valueX = x + labelWidth + 3;

            doc.setFont("helvetica", "bold");
            doc.text(field.label + ":", x, y);
            doc.setFont("helvetica", "normal");

            let rawValue =
              field.key !== undefined ? jsonContent[field.key] : field.value;
            if (
              rawValue === undefined &&
              jsonContent.condo?.[field.key] !== undefined
            ) {
              rawValue = jsonContent.condo[field.key];
            }

            let displayValue = "N/A";
            if (field.key === "hasCondos") {
              displayValue =
                rawValue === true
                  ? "Yes"
                  : rawValue === false
                    ? "No"
                    : "Not Specified";
            } else {
              displayValue = rawValue ?? "N/A";
            }

            doc.text(displayValue.toString(), valueX, y);
          }
        }
      }

      function drawProjectNameBox(doc, jsonContent) {
        const projectBoxX = 3;
        const projectBoxY = 32;
        const projectBoxW = 65;
        const projectBoxH = 7;

        // Background fill
        doc.setFillColor(53, 44, 29);
        doc.rect(projectBoxX, projectBoxY, projectBoxW, projectBoxH, "F");

        // Border
        doc.setDrawColor(0);
        doc.setLineWidth(0.2);
        doc.rect(projectBoxX, projectBoxY, projectBoxW, projectBoxH);

        // Text
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Project Name:", projectBoxX + 2, projectBoxY + 5);

        doc.setFont("helvetica", "bold");
        doc.text(
          jsonContent.projectname || "N/A",
          projectBoxX + 29,
          projectBoxY + 5
        );

        // Reset text color to black
        doc.setTextColor(0, 0, 0);
      }

      let pdfPageCount = 0;
      // Define closet details array (each as { label, value }). Adjust as needed.
      function drawClosetDetailPage(doc, jsonContent, jobID) {
        drawHeader(doc, logoBase64, jobID, true);

        //const jsonContent = JSON.parse(resultArray[i].jsoncontent);
        //pdfURLs.push({ [jsonContent.jobId] : [jsonContent.designSheetURL, jsonContent.pricePresentationURL] });
        const startY = 17 + 15 + 1;
        // Each table will have width = 90 mm and a height to fit the fields.
        const tableW = 204;
        // -- Address/Location Details Table --
        const clientAddrX = 3;
        const clientAddrY = startY + 43; // Starting Y position for the table
        //job site address
        const addrFieldStartY = clientAddrY + 8 + 1; // Add 5mm gap between header and fields

        doc.rect(clientAddrX, clientAddrY, tableW, 22);
        doc.setFont("helvetica", "bold");
        doc.text("Job Site Address", clientAddrX + 2, clientAddrY + 5);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        const addrFieldCols = 3;
        const addrRowHeight = 6;
        const addrRows = Math.ceil(
          extendedClientAddressFields.length / addrFieldCols
        );

        // Loop through the fields dynamically
        for (let col = 0; col < addrFieldCols; col++) {
          for (let row = 0; row < addrRows; row++) {
            const index = col * addrRows + row;
            if (index >= extendedClientAddressFields.length) break;
            const field = extendedClientAddressFields[index];

            let x = clientAddrX + 2 + col * (tableW / addrFieldCols);

            // Move the second column (col === 1) to the right by increasing the x position
            if (col === 1) {
              x = clientAddrX + 2 + col * (tableW / addrFieldCols) + 25; // Move 5mm to the right (adjust as needed)
            }
            if (col === 2) {
              x = clientAddrX + 2 + col * (tableW / addrFieldCols) + 15; // Move 5mm to the right (adjust as needed)
            }

            const y = addrFieldStartY + row * addrRowHeight;

            // Calculate dynamic width for label and value to fit within table
            const labelWidth = doc.getTextWidth(field.label + ":"); // Width of the label including the colon
            const valueX = x + labelWidth + 3; // Increase space between label and value (3mm gap)

            doc.setFont("helvetica", "bold");
            doc.text(field.label + ":", x, y);
            doc.setFont("helvetica", "normal");

            //const value = field.key in jsonContent ? jsonContent[field.key] : "N/A";
            //doc.text(value.toString(), valueX, y);
            //console.log("designatedCuttingArea from jsonContent:", jsonContent.cuttingarea);

            if (
              field.key !== undefined &&
              field.key !== null &&
              jsonContent[field.key] !== undefined &&
              jsonContent[field.key] !== null
            ) {
              logger.debug("PDF field:", field.key, field);

              // Step 1: Try to get from top-level
              let rawValue = jsonContent[field.key];

              // Step 2: If undefined, try from nested "condo" object
              if (
                rawValue === undefined &&
                jsonContent.condo?.[field.key] !== undefined
              ) {
                rawValue = jsonContent.condo[field.key];
              }

              // Step 3: Format boolean (e.g., hasCondos)
              let displayValue;
              if (field.key === "hasCondos") {
                if (rawValue === true) displayValue = "Yes";
                else if (rawValue === false) displayValue = "No";
                else displayValue = "Not Specified";
              } else {
                displayValue = rawValue ?? "N/A";
              }

              doc.text(displayValue.toString(), valueX, y);
            } else if (field.value !== undefined) {
              doc.text(field.value.toString(), valueX, y);
            }
          }
        }

        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(
          "ORDER CONFIRMATION",
          pageWidth / 2, // x-coordinate: center of page
          12, // y-coordinate: e.g. 15mm from top
          { align: "center" } // center alignment
        );

        // --- Project Name Box Below Logo ---

        const projectBoxX = 3; // same x as logo
        const projectBoxY = 32; // just below the logo (logo ends at y = 10 + 25 = 35)
        const projectBoxW = 65; // same width as logo
        const projectBoxH = 7; // small box height

        // Set background color (light grey)
        doc.setFillColor(53, 44, 29);
        doc.rect(projectBoxX, projectBoxY, projectBoxW, projectBoxH, "F"); // fill rectangle

        // Draw the rectangle
        doc.setDrawColor(0); // black border
        doc.setLineWidth(0.2);
        doc.rect(projectBoxX, projectBoxY, projectBoxW, projectBoxH);

        doc.setTextColor(255, 255, 255);

        // Add label
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Project Name:", projectBoxX + 2, projectBoxY + 5);

        // Add value (from localStorage or passed arg, fallback to "N/A")
        //const projectName = LocalStorageService.getProjectName?.() || "N/A"; // or pass as arg
        doc.setFont("helvetica", "bold");
        doc.text(
          jsonContent.projectname || "N/A",
          projectBoxX + 29,
          projectBoxY + 5
        );

        doc.setTextColor(0, 0, 0);
        const closetY = 23 + 17 + 59;
        const closetW = 204; // use nearly full width of page (A4 width ~210, with 5 mm margin each side)
        const closetH = 117;
        doc.rect(3, closetY, closetW, closetH);
        doc.setFont("helvetica", "bold");
        doc.text("Closet Details", 3 + 2, closetY + 5);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        const closetDetails = [
          {
            label: "Collection",
            value: jsonContent.collection + " - " + jsonContent.collectionclr,
          },
          {
            label: "Rod Qty & Style",
            value: jsonContent.rods.rodenabled
              ? `${jsonContent.rods.quantity} - ${jsonContent.rods.style} / ${jsonContent.rods.color}`
              : "N/A",
          },

          {
            label: "Door Qty & Deco",
            value: jsonContent.doors.doorenabled
              ? `${jsonContent.doors.quantity} - ${jsonContent.doors.decostyle !== "Contemporary"
                ? `${jsonContent.doors.decostyle} / ${jsonContent.doors.series} / ${jsonContent.doors.variant}`
                : jsonContent.doors.decostyle
              } / Color: ${jsonContent.doors.doorclosetcollection} - ${jsonContent.doors.doorcolor
              } `
              : "N/A",
          },
          /*{
            label: "Door Grip",
            value: jsonContent.doors.doorenabled
              ? jsonContent.doors.gripSeries
                ? `${jsonContent.doors.gripType} / ${jsonContent.doors.gripSeries} / ${jsonContent.doors.gripSize} / ${jsonContent.doors.gripColor}`
                : jsonContent.doors.gripType
              : "N/A",
          },*/
          {
            label: "Door Grip",
            value: jsonContent.doors.doorenabled
              ? jsonContent.doors.gripSeries
                ? jsonContent.doors.gripSeries === "Supplied By Customer"
                  ? `${jsonContent.doors.gripType} / ${jsonContent.doors.gripSeries}`
                  : `${jsonContent.doors.gripType} / ${jsonContent.doors.gripSeries} / ${jsonContent.doors.gripSize} / ${jsonContent.doors.gripColor}`
                : jsonContent.doors.gripType
              : "N/A",
          },
          {
            label: "Special Door",
            value: jsonContent.specialdoors.specialDoorEnabled
              ? `${jsonContent.specialdoors.specialDoorQuantity} - ${jsonContent.specialdoors.specialDoorType} /\n ${jsonContent.specialdoors.specialDoorSize} / ${jsonContent.specialdoors.specialDoorHeight} / ${jsonContent.specialdoors.specialDoorInsert} /\n ${jsonContent.specialdoors.specialDoorFrame} / ${jsonContent.specialdoors.specialDoorEdge}`
              : "N/A",
          },
          {
            label: "Special Door Grip",
            value:
              jsonContent.specialdoors.specialDoorEnabled &&
                jsonContent.specialdoors.specialDoorType === "TFL Doors"
                ? jsonContent.specialdoors.gripSeries
                  ? jsonContent.specialdoors.gripSeries === "Supplied By Customer"
                    ? `${jsonContent.specialdoors.gripType} / ${jsonContent.specialdoors.gripSeries}`
                    : `${jsonContent.specialdoors.gripType} / ${jsonContent.specialdoors.gripSeries} / ${jsonContent.specialdoors.gripSize} / ${jsonContent.specialdoors.gripColor}`
                  : jsonContent.specialdoors.gripType || "N/A"
                : "N/A",
          },

          {
            label: "Drawer Qty & Deco",
            value: jsonContent.drawers.drawerenabled
              ? `${jsonContent.drawers.quantity} - ${jsonContent.drawers.decostyle !== "Contemporary"
                ? `${jsonContent.drawers.decostyle} / ${jsonContent.drawers.series} / ${jsonContent.drawers.variant}`
                : jsonContent.drawers.decostyle
              } / \nBox Color: ${jsonContent.drawers.drawerclosetcollection
              } - ${jsonContent.drawers.drawercolor} /\n Front Color: ${jsonContent.drawers.frontclosetcollection
              } - ${jsonContent.drawers.drawerfrontcolor}`
              : "N/A",
          },
          {
            label: "Drawer Grip",
            value: jsonContent.drawers.drawerenabled
              ? jsonContent.drawers.gripSeries
                ? jsonContent.drawers.gripSeries === "Supplied By Customer"
                  ? `${jsonContent.drawers.gripType} / ${jsonContent.drawers.gripSeries}`
                  : `${jsonContent.drawers.gripType} / ${jsonContent.drawers.gripSeries} / ${jsonContent.drawers.gripSize} / ${jsonContent.drawers.gripColor}`
                : jsonContent.drawers.gripType
              : "N/A",
          },

          // Combine Rod Qty and Rod Style into one field

          // Combine Moulding Top and Moulding Bottom into one field
          {
            label: "Moulding",
            value: `${jsonContent.moulding.topfinish
              ? "Top: Yes (" +
              (jsonContent.moulding.top || "Not Selected") +
              ")"
              : "Top: No"
              }\n${jsonContent.moulding.bottomfinish
                ? "Bottom: Yes (" +
                (jsonContent.moulding.bottom || "Not Selected") +
                ")"
                : "Bottom: Regular Toe"
              }`,
          },

          {
            label: "Baseboard",
            value: jsonContent.existingBoard.remove
              ? "Remove"
              : jsonContent.existingBoard.notch
                ? "Notch"
                : "Notch",
          },

          // Add Is There Backing section
          {
            label: "Backing",
            value: jsonContent.isThereBacking ? "Yes" : "No",
          },

          // Add Is There drillinh section
          /* {
             label: "Drilling",
             value: jsonContent.isThereDrilling ? "Yes" : "No",
           },*/

          // Add Countertop details
          {
            label: "Countertop",
            value: jsonContent.counterTop.countertopenabled
              ? `${jsonContent.counterTop.type} / ${jsonContent.counterTop.color} / ${jsonContent.counterTop.edge}`
              : "N/A",
          },

          // Add Tear Out
          { label: "Tear Out", value: jsonContent.tearout },
        ];

        // Organize closet details in a 2-column grid.
        const closetCols = 2;
        const closetCellPadding = 2;
        const closetCellWidth = (closetW - (closetCols + 1) * closetCellPadding) / closetCols;
        const closetRows = Math.ceil(closetDetails.length / closetCols);
        const closetCellHeight = (closetH - 6 - closetCellPadding) / closetRows; // subtract header area (10 mm)

        // Render each closet detail
        let gridIndex = 0;
        for (let i = 0; i < closetDetails.length; i++) {
          const item = closetDetails[i];
          const isStopDrilling = item.label.startsWith("Stop Drill");
          const isCustomDrilling = isStopDrilling && jsonContent.isThereDrilling === "Custom";
          const col = gridIndex % closetCols;
          const row = Math.floor(gridIndex / closetCols);
          const cellX = 3 + closetCellPadding + col * (closetCellWidth + closetCellPadding);
          const cellY = closetY + 5 + closetCellPadding + row * closetCellHeight;
          let cellWidth = isCustomDrilling
            ? closetCellWidth * 2 + closetCellPadding
            : closetCellWidth;
          let cellHeight = closetCellHeight

          doc.setFont("helvetica", "bold");
          doc.text(closetDetails[i].label + ":", cellX + 1, cellY + 4);
          doc.setFont("helvetica", "normal");
          const valueText = (closetDetails[i].value ?? '').toString();
          const lineHeight = 4;
          const maxContentLines = 2;
          doc.rect(cellX, cellY, cellWidth, cellHeight);


          if (item.label.startsWith("Stop Drill")) {
            const wrapped = doc.splitTextToSize(valueText, cellWidth - 4);

            // Case 1: Fits within 2 lines
            if (wrapped.length <= maxContentLines) {
              wrapped.forEach((line, idx) => {
                doc.text(line, cellX + 1, cellY + 9 + idx * lineHeight);
              });
            } else {
              // First line prints normally
              doc.text(wrapped[0], cellX + 1, cellY + 9);

              // Second line must include content + TBC
              let secondLine = wrapped[1];
              const tbcText = "....TBC (Stop Drill)";
              const maxWidth = cellWidth - 4;

              // Measure bold TBC width
              doc.setFont("helvetica", "bold");
              const tbcWidth = doc.getTextWidth(tbcText);

              // Trim second line (normal font) until it fits with bold TBC
              doc.setFont("helvetica", "normal");
              while (
                doc.getTextWidth(secondLine) + tbcWidth > maxWidth &&
                secondLine.length > 0
              ) {
                secondLine = secondLine.slice(0, -1);
              }

              const yPos = cellY + 9 + lineHeight;

              // Print trimmed content
              doc.text(secondLine.trimEnd(), cellX + 1, yPos);

              // Print bold TBC right after content
              const contentWidth = doc.getTextWidth(secondLine.trimEnd());
              doc.setFont("helvetica", "bold");
              doc.text(tbcText, cellX + 1 + contentWidth, yPos);

              doc.setFont("helvetica", "normal");
            }

          } else {


            const posX = Number(cellX) + 1;
            const posY = Number(cellY) + (closetDetails[i].label === "Moulding" || closetDetails[i].label === "Drawer Qty & Deco" || closetDetails[i].label === "Special Door" ? 8 : 9);

            doc.text(valueText, posX, posY);
          }


          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          if (closetDetails[i].label === "Backing") {
            if (!window.backingPages) window.backingPages = new Set();
            window.backingPages.add(currentPage);
          }
          gridIndex += isCustomDrilling ? 2 : 1;
        }

        const additionalInfoBoxX = 3;
        const additionalInfoBoxY = closetY + closetH + 1;
        const additionalInfoBoxWidth = 204;
        const additionalInfoBoxHeight = 60;
        const columnGap = 4;
        const boxPadding = 3;

        // Calculate column width and center line
        const columnWidth = (additionalInfoBoxWidth - columnGap - 2 * boxPadding) / 2;
        const centerLineX = additionalInfoBoxX + boxPadding + columnWidth + columnGap / 2;

        // Draw outer rectangle
        doc.setLineWidth(0.1);
        doc.rect(additionalInfoBoxX, additionalInfoBoxY, additionalInfoBoxWidth, additionalInfoBoxHeight);

        // Draw center vertical divider
        doc.line(
          centerLineX,
          additionalInfoBoxY,
          centerLineX,
          additionalInfoBoxY + additionalInfoBoxHeight
        );

        // ========== LEFT COLUMN HEADER ==========
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("Stop Drill", additionalInfoBoxX + boxPadding, additionalInfoBoxY + boxPadding + 2);

        // ========== RIGHT COLUMN HEADER ==========
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("Additional Information", centerLineX + columnGap / 2, additionalInfoBoxY + boxPadding + 2);

        // Content font
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        const contentStartY = additionalInfoBoxY + boxPadding + 10;
        const lineHeight = 4;
        const maxLinesPerColumn = Math.floor((additionalInfoBoxHeight - (contentStartY - additionalInfoBoxY) - boxPadding) / lineHeight);

        // ========== LEFT COLUMN - Stop Drill Content ==========
        const leftColumnStartX = additionalInfoBoxX + boxPadding;
        let leftColumnY = contentStartY;

        // Display the drilling option value
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(jsonContent.isThereDrilling || "Not specified", leftColumnStartX, leftColumnY);
        leftColumnY += lineHeight;

        // Add drill details based on selection
        if (jsonContent.isThereDrilling === "Standard") {
          // For Standard: show drillDetails
          const standardDrillDetails = "Standard Option can include the following:\n" +
            "Double Hang: No holes to be drilled. \n" +
            "Medium Hang: 36\" of holes (18\" per shelf) - 2 additional holes below rod location. \n" +
            "Long Hang: 18\" of holes (for 1 shelf) - 2 additional holes below rod location.\n" +
            "Shelving Section: Holes still to be drilled.";

          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          const wrappedDetails = doc.splitTextToSize(standardDrillDetails, columnWidth);
          wrappedDetails.forEach((line, i) => {
            if (leftColumnY + i * lineHeight < additionalInfoBoxY + additionalInfoBoxHeight - boxPadding) {
              doc.text(line, leftColumnStartX, leftColumnY + i * lineHeight);
            }
          });
        } else if (jsonContent.isThereDrilling === "Custom") {
          // For Custom: show drillingtxt
          if (jsonContent.drillingtxt) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            const customNotes = `Notes: ${jsonContent.drillingtxt}`;
            const wrappedNotes = doc.splitTextToSize(customNotes, columnWidth);
            wrappedNotes.forEach((line, i) => {
              if (leftColumnY + i * lineHeight < additionalInfoBoxY + additionalInfoBoxHeight - boxPadding) {
                doc.text(line, leftColumnStartX, leftColumnY + i * lineHeight);
              }
            });
          }
        }

        // ========== RIGHT COLUMN - Additional Notes Content ==========
        // ========== RIGHT COLUMN - Additional Notes Content ==========
        const rightColumnStartX = centerLineX + columnGap / 2;

        // Calculate the actual available width for the right column
        const rightColumnWidth = additionalInfoBoxX + additionalInfoBoxWidth - rightColumnStartX - boxPadding;

        const additionalInfoText = jsonContent.additionalNotes || "No additional information provided.";

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        // Use the calculated right column width
        const rightColumnText = doc.splitTextToSize(additionalInfoText, rightColumnWidth);

        // Draw right column text (additional notes)
        rightColumnText.slice(0, maxLinesPerColumn).forEach((line, i) => {
          doc.text(line, rightColumnStartX, contentStartY + i * lineHeight);
        });

        // Designer Signature (on the left)
        doc.text("Designer Signature: __________________", 3 + 2, 290);

        // Client Signature (on the right)
        doc.text("Client Signature: __________________", 87, 290);

        doc.addPage();
        //doc.text("Client Signature: __________________", 35, 168);
      }

      // ---------------------- Accessories Section ----------------------
      function drawAccessoriesPage(doc, jsonContent, jobID) {
        if (
          (jsonContent.racks && jsonContent.racks.length > 0) ||
          (jsonContent.baskets && jsonContent.baskets.length > 0) ||
          (jsonContent.hooks && jsonContent.hooks.length > 0) ||
          (jsonContent.hampers && jsonContent.hampers.length > 0)
        ) {
          pdfPageCount += 1;
          const pageWidth4 = doc.internal.pageSize.getWidth();
          drawHeader(doc, logoBase64, jobID, false);
          drawProjectNameBox(doc, jsonContent);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(14);
          doc.text(
            "ACCESSORIES",
            pageWidth4 / 2, // x-coordinate: center of page
            12, // y-coordinate: e.g. 15mm from top
            { align: "center" } // center alignment
          );
          const accAddrX = 3;
          const accAddrY = 17 + 15 + 1 + 43; // Starting Y position for the table

          doc.rect(accAddrX, accAddrY, 204, 200);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(12);
          doc.text("Rack", accAddrX + 2, accAddrY + 5);

          function drawPair(doc, label, value, x, y) {
            doc.setFont("helvetica", "bold");
            doc.text(label, x, y);
            x += doc.getTextWidth(label);

            doc.setFont("helvetica", "normal");
            doc.text(value, x, y);

            return x + doc.getTextWidth(value);
          }

          let nextBasketY = printRackSection(doc, 85);
          let nextHooksY = printBasketSection(doc, nextBasketY);
          let nextHamperY = printHooksSection(doc, nextHooksY + 5);
          printHamperSection(doc, nextHamperY + 5);
          // Rack Section
          function printRackSection(doc, startY) {
            const lineHeight = doc.getTextDimensions("A").h;
            let y = startY + 2;
            if (jsonContent.racks.length > 0) {
              for (let j = 0; j < jsonContent.racks.length; j++) {
                const rackItem = jsonContent.racks[j];
                let x = 8;

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.text(j + 1 + ". ", x, y);
                x += doc.getTextWidth(j + 1 + ". ");

                x = drawPair(doc, "Qty: ", rackItem.rackquantity + "  ", x, y);
                x = drawPair(doc, "Type: ", rackItem.racktype + "  ", x, y);
                x = drawPair(doc, "Code: ", rackItem.rackcode + "  ", x, y);
                x = drawPair(doc, "Color: ", rackItem.rackcolour + "  ", x, y);
                drawPair(doc, "Size: ", rackItem.racksize, x, y);
                y += lineHeight + 2;
              }
            } else {
              doc.setFont("helvetica", "bold");
              doc.text(" N/A ", 10, startY);
            }
            //return startY + jsonContent.racks.length * lineHeight + 8;   // return next Y
            return y + 3;
          }

          // Basket Section
          function printBasketSection(doc, startY) {
            const lineHeight = doc.getTextDimensions("A").h + 2;
            let y = startY;

            // Title
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("Basket:", 5, y + 2);

            y += lineHeight + 2;

            if (jsonContent.baskets.length > 0) {
              for (let j = 0; j < jsonContent.baskets.length; j++) {
                const basketItem = jsonContent.baskets[j];

                let x = 8;

                // First line
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.text(`${j + 1}.`, x, y);
                x += doc.getTextWidth(`${j + 1}. `);

                x = drawPair(
                  doc,
                  "Qty: ",
                  basketItem.basketquantity + "  ",
                  x,
                  y
                );
                x = drawPair(doc, "Type: ", basketItem.baskettype + "  ", x, y);
                drawPair(doc, "Size: ", basketItem.basketsize, x, y);

                // Second line (Liner info)
                y += lineHeight;
                x = 10;
                x = drawPair(
                  doc,
                  "Liner: ",
                  basketItem.basketliner + "  ",
                  x,
                  y
                );
                drawPair(doc, "L-Size: ", basketItem.basketlinersize, x, y);

                // Blank line between items
                y += lineHeight + 3;
              }
            } else {
              doc.setFont("helvetica", "bold");
              doc.text("N/A", 10, y);
              y += lineHeight;
            }
            return y + 4;
          }

          // Hooks Section
          function printHooksSection(doc, startY) {
            const lineHeight = doc.getTextDimensions("A").h;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("Hooks:", 5, startY);
            let y = startY + lineHeight + 3;

            if (jsonContent.hooks.length > 0) {
              for (let h = 0; h < jsonContent.hooks.length; h++) {
                const item = jsonContent.hooks[h];
                let x = 8;

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.text(h + 1 + ". ", x, y);
                x += doc.getTextWidth(h + 1 + ". ");

                x = drawPair(doc, "Type: ", item.hooktype + "  ", x, y);
                x = drawPair(doc, "Color: ", item.hookcolour + "  ", x, y);
                x = drawPair(
                  doc,
                  "Quantity: ",
                  item.hookspercleatQuantity + "  ",
                  x,
                  y
                );
                drawPair(doc, "Sets: ", item.cleatQuantity, x, y);
                y += lineHeight + 2;
              }
            } else {
              doc.setFont("helvetica", "bold");
              doc.text(" N/A ", 10, startY + lineHeight + 2);
            }
            //return startY + jsonContent.hooks.length * lineHeight + 8;
            return y + 4;
          }

          function printHamperSection(doc, startY) {
            const lineHeight = doc.getTextDimensions("A").h;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("Hamper:", 5, startY);

            let y = startY + lineHeight + 4;
            if (jsonContent.hampers.length > 0) {
              for (let i = 0; i < jsonContent.hampers.length; i++) {
                const item = jsonContent.hampers[i];
                let x = 8;

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.text(i + 1 + ". ", x, y);
                x += doc.getTextWidth(i + 1 + ". ");

                x = drawPair(
                  doc,
                  "Quantity: ",
                  item.hamperquantity + "  ",
                  x,
                  y
                );
                x = drawPair(doc, "Type: ", item.hampertype + "  ", x, y);
                x = drawPair(doc, "Code: ", item.hampercode + "  ", x, y);
                x = drawPair(doc, "Color: ", item.hampercolour + "  ", x, y);
                drawPair(doc, "Size: ", item.hampersize, x, y);
                y += lineHeight + 2;
              }
            } else {
              doc.setFont("helvetica", "bold");
              doc.text(" N/A ", 10, startY + lineHeight + 2);
              y += lineHeight;
            }
            return y + 4;
          }

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          // Designer Signature
          doc.text("Designer Signature: __________________", 5, 290);

          // Client Signature
          doc.text("Client Signature: __________________", 87, 290);
          doc.addPage();
        }
      }
      //-----------------------Kitchen Space Section-----------------------
      //-----------------------Kitchen Space Section-----------------------
      function drawKitchenSpacePage(doc, jsonContent, jobID) {
        if (
          (jsonContent.kitchenSpacesEnabled === true)
        ) {
          const kitchenPagesBefore = doc.getNumberOfPages();
          const pageWidth4 = doc.internal.pageSize.getWidth();
          const kp = jsonContent.kitchenPricing;
          drawHeader(doc, logoBase64, jobID, false);
          drawJobSiteAddress(doc, jsonContent, extendedClientAddressFields);
          //drawProjectNameBox(doc, jsonContent);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(14);
          doc.text(
            "KITCHEN SPACES",
            pageWidth4 / 2, // x-coordinate: center of page
            12, // y-coordinate: e.g. 15mm from top
            { align: "center" } // center alignment
          );



          drawKitchenPricingPage(doc, jsonContent, jobID);


          function drawKitchenPricingPage(doc, jsonContent, jobID) {

            // Rows in the same order/grouping as the reference pricing sheet
            // Accessories moved to the end and given a bigger cell (large textarea field)
            const rows = [
              { label: "Cabinet Construction", value: kp.cabinetConstruction },
              { label: "Door Style", value: kp.doorStyle },
              { label: "Door Material", value: kp.doorMaterial },
              { label: "Door Finish", value: kp.doorFinish },
              { label: "Finish Ends Dummy Doors", value: kp.finishEndsDummyDoors },
              { label: "Finish Ends Flat Panels", value: kp.finishEndsFlatPanels },
              { label: "Finish Ends Columns", value: kp.finishEndsColumns },
              { label: "Finish Interior Uppers", value: kp.finishInteriorUppers },
              { label: "Glass Type", value: kp.glassTypeDetail },
              { label: "Drawer Boxes", value: kp.drawerBoxesDetail },
              { label: "Glides", value: kp.glidesDetail },
              { label: "Opening Hardware", value: kp.openingHardwareDetail },
              { label: "Crown System", value: kp.crownSystemDetail },
              { label: "Facia Panel", value: kp.faciaPanelDetail },
              { label: "Décor Rail", value: kp.decorRailDetail },
              { label: "Light Valance", value: kp.lightValanceDetail },
              { label: "Undermount Panel", value: kp.undermountPanelDetail },
              { label: "Custom Canopy", value: kp.customCanopyDetail },
              { label: "Appliances Door Panels", value: kp.appliancesDoorPanelsDetail },
              { label: "Hinges", value: kp.hingesDetail },
              { label: "Counter Tops", value: kp.counterTopsDetail },
              { label: "Electrical", value: kp.electricalDetail },
              { label: "Puck Lights", value: kp.puckLightsDetail },
              { label: "Plumbing", value: kp.plumbingDetail },
              { label: "Delivery and Installation", value: kp.deliveryInstallationDetail },
              { label: "Accessories", value: kp.accessoriesDetail, big: true },
              { label: "Special Details", value: kp.kitchenSpecialDetails, big: true },
            ];

            const tableX = 3;
            const tableTop = 99;
            const tableW = 204;
            const col1W = 58; // Description column
            const col2W = tableW - col1W; // Value column
            const lineHeight = 3.6;
            const cellPadding = 1.5;
            const bigCellMinHeight = 14.5; // guaranteed larger cell for Accessories

            // Header row
            doc.setFillColor(255, 255, 255);
            doc.setDrawColor(0);
            doc.setLineWidth(0.1);
            const headerH = 7;
            doc.rect(tableX, tableTop, col1W, headerH, "F");
            doc.rect(tableX + col1W, tableTop, col2W, headerH, "F");
            doc.rect(tableX, tableTop, tableW, headerH);
            doc.line(tableX + col1W, tableTop, tableX + col1W, tableTop + headerH);

            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text("Description", tableX + cellPadding, tableTop + 5);
            doc.text("Details", tableX + col1W + cellPadding, tableTop + 5);
            doc.setTextColor(0, 0, 0);

            let y = tableTop + headerH;
            doc.setFontSize(8);

            rows.forEach((row) => {
              const valueText = (row.value && row.value.trim()) ? row.value : "N/A";
              const wrappedValue = doc.splitTextToSize(valueText, col2W - cellPadding * 1);
              const rowLines = Math.max(wrappedValue.length, 1);
              const minHeight = row.big ? bigCellMinHeight : 5.5;
              const rowH = Math.max(rowLines * lineHeight + cellPadding * 1.3, minHeight);

              // Page-break check
              if (y + rowH > 285) {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(9);
                doc.text("Designer Signature: __________________", 5, 290);
                doc.text("Client Signature: __________________", 87, 290);
                doc.addPage();
                drawHeader(doc, logoBase64, jobID, false);
                drawJobSiteAddress(doc, jsonContent, extendedClientAddressFields);
                y = 99;
                doc.setFontSize(8);
              }

              // Draw row borders
              doc.rect(tableX, y, col1W, rowH);
              doc.rect(tableX + col1W, y, col2W, rowH);

              // Label
              doc.setFont("helvetica", "bold");
              doc.text(row.label, tableX + cellPadding, y + 4.5);

              // Value (wrapped)
              doc.setFont("helvetica", "normal");
              doc.text(wrappedValue, tableX + col1W + cellPadding, y + 4.5);

              y += rowH;
            });


            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            // Designer Signature
            doc.text("Designer Signature: __________________", 5, 290);

            // Client Signature
            doc.text("Client Signature: __________________", 87, 290);
            doc.addPage();

          }

          // Count however many pages this section actually added — it can overflow
          // to a second (or third) page when the kitchen details are long, instead of
          // always being exactly one page. This keeps the "payment page" index
          // calculation further down in this file correct, which is what decides
          // whether a given page's signature box is placed near the top (payment
          // layout) or at the bottom (normal layout) — an incorrect count here was
          // causing the kitchen overflow page to be mistaken for the payment page.
          const kitchenPagesAfter = doc.getNumberOfPages();
          pdfPageCount += (kitchenPagesAfter - kitchenPagesBefore);
        }
      }


      for (const idx of closetIndices) {
        drawClosetDetailPage(doc, parsedEntries[idx], jobID);
      }

      // Pass 2 — Accessories, only for closet entries, and only when at least one
      // has racks/baskets/hooks/hampers. Runs after ALL closet detail pages and
      // before any kitchen pages.
      for (const idx of closetIndices) {
        drawAccessoriesPage(doc, parsedEntries[idx], jobID);
      }

      // Pass 3 — Kitchen Spaces, always last.
      for (const idx of kitchenIndices) {
        drawKitchenSpacePage(doc, parsedEntries[idx], jobID);
      }

      //-------------------- End Of Payment Information-----------------------------
      // ----------------------
      // 5. Payment Section - Dynamically Render Payment Method
      // ----------------------

      const pageWidth4 = doc.internal.pageSize.getWidth();
      drawHeader(doc, logoBase64, jobID, true);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(
        "ORDER CONFIRMATION",
        pageWidth4 / 2, // x-coordinate: center of page
        12, // y-coordinate: e.g. 15mm from top
        { align: "center" } // center alignment
      );

      // Capture the actual page number the payment/letter/signature content
      // starts on. Used later (instead of the closetIndices/pdfPageCount
      // formula) to place the DocuSeal signature areas correctly even if
      // this section overflows onto an extra page below.
      const paymentPageNumber = doc.internal.getCurrentPageInfo().pageNumber;

      const signY = 76; // + 36 + 5;
      const signW = 204;
      const signH = 170; // use extra space
      // Sample dynamic values
      const today = moment().format("MMMM D, YYYY");
      const designer = designerName;
      const client = clientName;
      const totalAmount = (
        (hasClosetForms ? Number(closetFinance.total || 0) : 0) +
        (hasKitchenForms ? Number(kitchenFinance.total || 0) : 0)
      ).toFixed(2);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);

      const letterLines = [
        ``,
        `${today}`,
        ``,
        //`RE: ${projectName}`,
        ``,
        `Dear ${clientName},`,
        `We are pleased to confirm the order for total ${closetsFormCount} Products in the total amount of ($${totalAmount}).`,
        ``,
        `Project Specifications:`,
      ];

      doc.text(letterLines, 3 + 2, signY + 5);

      const signText =
        "Please review this Order Confirmation and the attached Terms and Conditions, and sign below to indicate your acceptance of both. Once we receive your signed copy, production of your order will begin within three (3) business days. We will contact you to schedule an installation date.";

      // Split the text into lines that fit within the box width
      const wrappedText = doc.splitTextToSize(signText, signW - 4); // 4 mm padding inside box


      const cols = 4;
      const rows = 5;

      const tableX = 3;
      const tableY = signY + 35; // Adjust this to push it below the letter content
      const tableW = signW;
      const tableH = 50; // Max you can use: signY + signH - tableY

      const cellW = tableW / cols;
      const cellH = tableH / rows;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      //doc.text("B = Backing , D = Drilling", 170, 109)

      // 1. Collect all project names (from all resultArray[i].jsoncontent arrays)
      const allProjectNames = [];

      for (let i = 0; i < resultArray.length; i++) {
        const jsonContent = parsedEntries[i];
        // If jsonContent is an array, extract projectname from each item
        if (Array.isArray(jsonContent)) {
          jsonContent.forEach((item) => {
            if (item.projectname) {
              allProjectNames.push(item.projectname);
            }
          });
        }
        else if (jsonContent.kitchenSpacesEnabled === true) {
          // Kitchen entries never collect a Project Name — push a label instead
          // so this list stays 1:1 with resultArray order (the backing/drilling
          // lookup just below indexes back into resultArray by this position).
          allProjectNames.push("Kitchen Space");
        } else if (jsonContent.projectname) {
          allProjectNames.push(jsonContent.projectname);
        }
      }

      // Limit to 20 names for a 5x4 table
      const projectNames = allProjectNames.slice(0, 20);

      // Draw horizontal lines
      for (let r = 0; r <= rows; r++) {
        const y = tableY + r * cellH;
        doc.line(tableX, y, tableX + tableW, y);
      }

      // Draw vertical lines
      for (let c = 0; c <= cols; c++) {
        const x = tableX + c * cellW;
        doc.line(x, tableY, x, tableY + tableH);
      }

      // Fill table with project names
      let index = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const name = projectNames[index] || "";
          const number = `${index + 1}.`;

          const textX = tableX + c * cellW + 2;
          let textY = tableY + r * cellH + 5;

          // Draw number in bold
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(number, textX, textY);

          // Draw project name in normal, with slight offset
          doc.setFont("helvetica", "bold");
          doc.text(name, textX + doc.getTextWidth(number) + 1, textY);

          // 👉 Add selections ONLY if projectname exists
          if (name) {
            const projectData = parsedEntries[index] || {};

            if (projectData.kitchenSpacesEnabled !== true) {
              textY += 4;
              doc.setFontSize(8);
              doc.setFont("helvetica", "normal");
              const backingText = projectData.isThereBacking ? "Backing: Yes" : "Backing: No";
              doc.text(backingText, textX + 3, textY);

              const drillOffset = doc.getTextWidth(backingText) + 5;
              const drillingText =
                (projectData.isThereDrilling === "Standard" || projectData.isThereDrilling === "Custom")
                  ? "Stop Drilling: Yes"
                  : "Stop Drilling: No";
              doc.text(drillingText, textX + 3 + drillOffset, textY);
            }
          }
          index++;
        }
      }

      // ---- Payment Made By (unchanged from your version, just capturing where it ends) ----
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Payment Made By", 5, tableY + 58);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      let paymentMethodY = tableY + 68;

      if (hasClosetForms) {
        doc.text(`Closet Payment Method: ${closetFinance.paymentMethod || "N/A"}`, 3 + 2, paymentMethodY);
        paymentMethodY += 5;
        if (closetFinance.paymentMethod === "Cheque") {
          doc.text(`Closet Cheque Number: ${closetFinance.chequeNumber || "N/A"}`, 3 + 2, paymentMethodY);
          paymentMethodY += 10;
        }
      }

      if (hasKitchenForms) {
        doc.text(`Kitchen Payment Method: ${kitchenFinance.paymentMethod || "N/A"}`, 3 + 2, paymentMethodY);
        paymentMethodY += 5;
        if (kitchenFinance.paymentMethod === "Cheque") {
          doc.text(`Kitchen Cheque Number: ${kitchenFinance.chequeNumber || "N/A"}`, 3 + 2, paymentMethodY);
          paymentMethodY += 10;
        }
      }

      // ---- Financial Summary — Kitchen / Closet / (3rd table) side by side ----
      const finBoxW = 45;
      // ---- Financial Summary — TOTAL (wide, on top) / Kitchen + Closet (sub-tables below) ----
      const finLabelW = 30;
      const finValueW = 18;
      const finGap = 12;
      const subTableW = finLabelW + finValueW; // 48 — Kitchen/Closet box width

      const finLeftX = 80;                          // Kitchen — left sub-table, aligned under TOTAL's left edge
      const finMidX = finLeftX + subTableW + finGap; // Closet — right sub-table, aligned under TOTAL's right edge

      // TOTAL spans the same total width as (Kitchen box + gap + Closet box),
      // so it visually reads as the parent table sitting above both sub-tables.
      const totalTableW = subTableW + finGap + subTableW; // 108
      const totalLabelW = totalTableW - finValueW;        // keep value column same width as sub-tables

      const finStartY = paymentMethodY + 8;

      doc.setDrawColor(0);
      doc.setLineWidth(0.2);

      // Draws ONE box-style block. `labelW`/`valueW` are parameterized so the
      // same drawer can render both the narrow Kitchen/Closet boxes and the
      // wider TOTAL box without duplicating the row logic.
      const drawFinanceBoxBlock = (
        startX,
        startY,
        block,
        heading,
        depositRows,
        { showStatusCard = true, labelW = finLabelW, valueW = finValueW } = {}
      ) => {
        let y = startY;

        if (heading) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(heading, startX, y);
          y += 3.5; // ↓ was 5 — tighter gap between title and the table below it
        }

        const finRowH = 4;

        const drawFinRow = (label, value, { bold = true, italic = false, fontSize = 8 } = {}) => {
          doc.rect(startX, y, labelW, finRowH);
          doc.rect(startX + labelW, y, valueW, finRowH);

          doc.setFont("helvetica", italic ? "italic" : (bold ? "bold" : "normal"));
          doc.setFontSize(fontSize);

          const labelLines = doc.splitTextToSize(label, labelW - 3);
          doc.text(labelLines[0], startX + 1.5, y + finRowH / 2 + 1.2);

          const valueText = `$${value || "0.00"}`;
          const valueWidth = doc.getTextWidth(valueText);
          doc.text(valueText, startX + labelW + valueW - 1.5 - valueWidth, y + finRowH / 2 + 1.2);

          y += finRowH;
        };

        drawFinRow("SUBTOTAL", block.subtotal, { bold: true, fontSize: 8 });
        drawFinRow(statusCard ? "HST (5%)" : "HST (13%)", block.hst, { bold: true, fontSize: 8 });
        drawFinRow("GRAND TOTAL", block.total, { bold: true, fontSize: 8 });

        depositRows.forEach((row) => drawFinRow(row.label, row.value, row.opts));

        if (statusCard && showStatusCard) {
          doc.rect(startX, y, labelW + valueW, finRowH);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          const cardLabel = `Status Card #: ${statusCardNumber || "N/A"}`;
          const cardLines = doc.splitTextToSize(cardLabel, labelW + valueW - 3);
          doc.text(cardLines[0], startX + 1.5, y + finRowH / 2 + 1.2);
          y += finRowH;
        }

        y += 3;
        return y;
      };

      const kitchenDepositRows = (block) => [
        { label: "50% DEPOSIT", value: block.deposit, opts: { bold: false, fontSize: 7 } },
        { label: "40% DELIVERY", value: block.progressPayment, opts: { bold: false, italic: true, fontSize: 7 } },
        { label: "10% COMPLETION", value: block.balanceDue, opts: { bold: false, italic: true, fontSize: 7 } },
      ];

      const closetDepositRows = (block) => [
        {
          label: block.depositOption ? `${block.depositOption} DEPOSIT` : "DEPOSIT",
          value: block.deposit,
          opts: { bold: false, fontSize: 7 },
        },
        { label: "BALANCE DUE", value: block.balanceDue, opts: { bold: false, italic: true, fontSize: 7 } },
      ];

      // ── Fixed finance-block height budget ──────────────────────────────
      // The signature/letter content below needs to start at the SAME Y
      // regardless of whether this job has Kitchen only, Closet only, or
      // both — otherwise the DocuSeal signature field (calibrated to one
      // fixed y) drifts out of place depending on which tables were drawn.
      // So we always reserve the height of the TALLEST possible case (both
      // present: TOTAL block + Kitchen's 3-deposit-row sub-table + status
      // row), and let the shorter cases leave blank space below their
      // ── Fixed finance-block height budget ──────────────────────────────
      const FIN_HEADER_H = 3.5;      // heading -> first row gap
      const FIN_ROW_H = 4;           // one table row
      const FIN_SUBTABLES_GAP = 4;   // gap between TOTAL and the sub-tables row
      const FIN_HST_GAP = 6;         // clear space between bottom of tables and the H.S.T.# line
      const FIN_TRAILING_SPACE = 5;  // space reserved AFTER the H.S.T.# line, before letter content

      // TOTAL block: header + 3 rows (subtotal/hst/grand total), no deposit rows, no status card
      const totalBlockH = FIN_HEADER_H + 3 * FIN_ROW_H;

      // Kitchen sub-table: header + 3 rows + 3 deposit rows + status row (worst case)
      const kitchenBlockH = FIN_HEADER_H + 3 * FIN_ROW_H + 3 * FIN_ROW_H + (statusCard ? FIN_ROW_H : 0);

      // Closet sub-table: header + 3 rows + 2 deposit rows + status row
      const closetBlockH = FIN_HEADER_H + 3 * FIN_ROW_H + 2 * FIN_ROW_H + (statusCard ? FIN_ROW_H : 0);

      // Tallest of the two sub-tables determines that row's height.
      const tallestSubTableH = Math.max(kitchenBlockH, closetBlockH);

      // Actual bottom Y of whichever tables get drawn (TOTAL + tallest sub-table,
      // or just the single table in the Kitchen-only/Closet-only case).
      const finBlockTopY = tableY + 58;
      const bothPresentBottomY = finBlockTopY + totalBlockH + FIN_SUBTABLES_GAP + tallestSubTableH;
      const singleTableBottomY = finBlockTopY + tallestSubTableH; // single table has no TOTAL block above it

      // Full fixed budget (used for finY): reserve up to the "both present"
      // bottom either way, plus a real gap before H.S.T.#, plus trailing space.
      const FIXED_FIN_HEIGHT =
        (bothPresentBottomY - finBlockTopY) + FIN_HST_GAP + FIN_TRAILING_SPACE;

      let finY;
      let finTablesBottomY;

      if (hasKitchenForms && hasClosetForms) {
        const combinedSubtotal = (Number(kitchenFinance.subtotal || 0) + Number(closetFinance.subtotal || 0)).toFixed(2);
        const combinedHst = (Number(kitchenFinance.hst || 0) + Number(closetFinance.hst || 0)).toFixed(2);
        const combinedTotal = (Number(kitchenFinance.total || 0) + Number(closetFinance.total || 0)).toFixed(2);
        const combinedBlock = { subtotal: combinedSubtotal, hst: combinedHst, total: combinedTotal };

        const totalEndY = drawFinanceBoxBlock(
          finLeftX, finBlockTopY, combinedBlock, "TOTAL", [],
          { showStatusCard: false, labelW: totalLabelW, valueW: finValueW }
        );

        const subTablesY = totalEndY + FIN_SUBTABLES_GAP;

        drawFinanceBoxBlock(finLeftX, subTablesY, kitchenFinance, "KITCHEN PAYMENT", kitchenDepositRows(kitchenFinance));
        drawFinanceBoxBlock(finMidX, subTablesY, closetFinance, "CLOSET PAYMENT", closetDepositRows(closetFinance));

        finTablesBottomY = bothPresentBottomY;
        finY = finBlockTopY + FIXED_FIN_HEIGHT;
      } else if (hasKitchenForms) {
        const finSingleX = finMidX;
        drawFinanceBoxBlock(finSingleX, finBlockTopY, kitchenFinance, "KITCHEN PAYMENT", kitchenDepositRows(kitchenFinance));
        finTablesBottomY = singleTableBottomY;
        finY = finBlockTopY + FIXED_FIN_HEIGHT;
      } else if (hasClosetForms) {
        const finSingleX = finMidX;
        drawFinanceBoxBlock(finSingleX, finBlockTopY, closetFinance, "CLOSET PAYMENT", closetDepositRows(closetFinance));
        finTablesBottomY = singleTableBottomY;
        finY = finBlockTopY + FIXED_FIN_HEIGHT;
      } else {
        finTablesBottomY = finStartY;
        finY = finStartY;
      }

      // H.S.T.# printed ONCE, shared — positioned a clear FIN_HST_GAP below
      // the actual bottom of whichever tables were drawn (Kitchen's status
      // row, since it's the tallest, when both are present), instead of
      // landing exactly on that row's border.
      const finHstX = (hasKitchenForms && hasClosetForms) ? finLeftX : finMidX;
      const finHstY = finTablesBottomY + FIN_HST_GAP;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("H.S.T.# 801434705", finHstX, finHstY);
      // ── Everything below (letter paragraph, Sincerely, signatures) was
      // positioned assuming a short, single finance block. One block, two
      // stacked blocks, or a tall block can all push past that fixed
      // budget — shift the rest down by however much finY overflowed the
      // old fixed position (signY + 130), and grow the outer box to match,
      // instead of letting them collide.
      const finExtraShift = Math.max(0, (finY + 5) - (signY + 130));

      // If the combined finance block(s) push the letter/signature content
      // too close to (or past) the bottom of the page — which can happen
      // when a job has both closet and kitchen forms — start a fresh page
      // for that remaining content instead of letting it overflow.
      const pageBottomLimit = doc.internal.pageSize.height - 15;
      let letterBoxY = signY;
      let letterShift = finExtraShift;
      let letterPageBroke = false;

      if (signY + 145 + finExtraShift + 25 > pageBottomLimit) {
        doc.addPage();
        pdfPageCount += 1;
        drawHeader(doc, logoBase64, jobID, true);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text("ORDER CONFIRMATION", pageWidth4 / 2, 12, { align: "center" });
        letterBoxY = signY;
        letterShift = 0;
        letterPageBroke = true;
      }

      doc.rect(3, letterBoxY, signW, signH + letterShift);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);

      doc.text(wrappedText, 3 + 2, letterBoxY + 130 + letterShift); // 2 mm left padding
      doc.text("Sincerely,", 3 + 2, letterBoxY + 143 + letterShift);
      doc.text("Dream Spaces", 3 + 2, letterBoxY + 147 + letterShift);
      doc.setFont("helvetica", "normal");

      // Adjust the x and Y positions for signatures and dates
      const sigStartY = letterBoxY + 145 + letterShift; // Starting Y position for the signature fields
      const nameStartY = sigStartY + 15; // Y position for the Date fields

      // Designer Signature (on the left)
      doc.text("Designer Signature: __________________", 3 + 2, sigStartY + 8);
      // Designer Date (below the signature)
      doc.text("Designer Name: __________________", 3 + 2, nameStartY + 8);

      // Client Signature (on the right)
      doc.text(
        "Client Signature: __________________",
        3 + signW - 120,
        sigStartY + 8
      );
      // Client Date (below the signature)
      doc.text(
        "Client Name: __________________",
        3 + signW - 120,
        nameStartY + 8
      );

      doc.text(designer, 35, nameStartY + 8);
      doc.text(client, 110, nameStartY + 8);


      doc.addPage([pageWidth, doc.internal.pageSize.height], "p");

      //-------------------- End Of Payment Information-----------------------------

      //------------------------ Start Backing & Drilling Page -------------------------

      for (const j of closetIndices) {
        const jsonContent = parsedEntries[j];
        //
        // Backing Images Page
        //
        drawHeader(doc, logoBase64, jobID, true);
        drawProjectNameBox(doc, jsonContent);
        drawJobSiteAddress(doc, jsonContent, extendedClientAddressFields);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(
          "ORDER CONFIRMATION",
          pageWidth / 2, // x-coordinate: center of page
          12, // y-coordinate: e.g. 15mm from top
          { align: "center" } // center alignment
        );

        // Layout
        const boxX = 3; // same left margin as the box above
        const boxY = 110; // top of this section (images start here)
        const gapBetweenBoxes = 4; // gap between the two columns
        const innerPad = 6; // inner padding inside columns
        const imageHeight = 95; // ↑ increased image height
        const imageWidth = 95; // ↓ slightly narrower than column inner width

        // ---------- OUTER BOX DIMENSIONS (same width as the box above) ----------
        const outerX = boxX;
        const outerY = boxY - 10; // small top padding above images
        const outerWidth = 204; // full width minus side margins

        // Split into two equal columns inside the outer box
        const colWidth = (outerWidth - gapBetweenBoxes) / 2;
        const colInnerWidth = colWidth - innerPad * 2; // usable width inside each column

        // ---------- LEFT COLUMN: BACKING ----------
        const leftColX = outerX;
        const leftImgX = leftColX + (colWidth - imageWidth) / 2; // centered in column
        const leftImgY = boxY;

        doc.addImage(
          jsonContent.isThereBacking ? backingyesBase64 : backingnoBase64,
          "PNG",
          leftImgX + 1,
          leftImgY,
          imageWidth,
          imageHeight
        );

        doc.text("Backing", 47, 106);
        doc.text("Stop Drilling", 138, 106);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(0);

        const backingText =
          "Closet backing lines the back wall of the system to create a smooth, uniform surface and a clean backdrop for your project. " +
          "It conceals any imperfections, paint variations, or uneven textures on the wall, ensuring that the focus stays on the design " +
          "of the closet itself. Adding backing gives the system a truly polished, built-in appearance and elevates the overall presentation " +
          "of the space. When chosen, it transforms a simple storage area into a refined, custom feature that feels intentional and complete.";

        const leftTextX = leftColX + innerPad; // align text to column inner edge
        const leftTextY = leftImgY + imageHeight + 8;
        const leftWrapped = doc.splitTextToSize(backingText, colInnerWidth);
        doc.text(leftWrapped, leftTextX, leftTextY);

        // selection bar under text
        const leftDims = doc.getTextDimensions(leftWrapped);
        const leftBtnTop = leftTextY + leftDims.h + 2;

        const backingStatement = jsonContent.isThereBacking
          ? "Yes, I have selected backing and agree to pay the\n additional cost."
          : "No, I have not selected backing and understand it will\n not be included.";

        const backingBoxW = 102; // full column width
        const backingBoxH = 13;

        if (jsonContent.isThereBacking) {
          doc.setFillColor(53, 44, 29);
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setFillColor(211, 211, 211);
          doc.setTextColor(0, 0, 0);
        }

        // draw full-width bar aligned to column inner edge
        doc.rect(leftColX, leftBtnTop, backingBoxW, backingBoxH, "F");

        // horizontally & vertically center the text in the bar
        const backingDims = doc.getTextDimensions(backingStatement);
        const backingTextY = leftBtnTop + (backingBoxH + backingDims.h) / 2 - 1;
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text(backingStatement, leftColX + 6, backingTextY - 1);

        // bottom of left column content
        const leftBottom = leftBtnTop + backingBoxH;

        // ---------- RIGHT COLUMN: STOP DRILL ----------
        const rightColX = outerX + colWidth + gapBetweenBoxes;
        const rightImgX = rightColX + (colWidth - imageWidth) / 2; // centered in column
        const rightImgY = boxY;

        doc.addImage(
          (jsonContent.isThereDrilling === "Standard" || jsonContent.isThereDrilling === "Custom")
            ? drillingyesBase64
            : drillingnoBase64,
          "PNG",
          rightImgX - 1,
          rightImgY,
          imageWidth,
          imageHeight
        );

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(0);

        const drillText =
          "Stop Drill is a customization option that removes the standard system holes along the panels, leaving only the necessary holes for shelves, rods, and other additions. " +
          "This creates a sleek, seamless look by eliminating the rows of unused holes that can distract from the overall design. " +
          "Choosing Stop Drill gives the closet a cleaner, more refined appearance and highlights the custom nature of the installation, " +
          "making the system feel more like built-in furniture than modular storage.";

        const rightTextX = rightColX + innerPad; // align text to column inner edge
        const rightTextY = rightImgY + imageHeight + 8;
        const rightWrapped = doc.splitTextToSize(drillText, colInnerWidth);
        doc.text(rightWrapped, rightTextX, rightTextY);

        const rightDims = doc.getTextDimensions(rightWrapped);
        const rightBtnTop = rightTextY + rightDims.h + 2;
        let drillStatement = "";
        let drillDetails = ""; // separate variable for the detailed list

        if (jsonContent.isThereDrilling === "No") {
          drillStatement =
            "No, I have not selected stop drilling and understand\n" +
            "it will not be included.";
        }

        if (jsonContent.isThereDrilling === "Standard") {
          drillStatement =
            "Yes, I have selected STANDARD stop drilling and agree\n" +
            "to pay the additional cost.";

          drillDetails = "";
        }

        if (jsonContent.isThereDrilling === "Custom") {
          drillStatement =
            "Yes, I have selected CUSTOM stop drilling and agree\n" +
            "to pay the additional cost.";

          drillDetails = "";
        }

        const drillBoxW = 102;
        const drillBoxH = 14;

        if (jsonContent.isThereDrilling === "Standard" || jsonContent.isThereDrilling === "Custom") {
          doc.setFillColor(53, 44, 29);
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setFillColor(211, 211, 211);
          doc.setTextColor(0, 0, 0);
        }

        // draw full-width bar
        doc.rect(rightColX - 2, rightBtnTop, drillBoxW, drillBoxH, "F");

        // Prepare wrapped text
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        const wrappedStatement = doc.splitTextToSize(drillStatement, colInnerWidth);

        // Estimate heights
        const statementDims = doc.getTextDimensions(wrappedStatement);
        let detailsDims = { h: 0 };
        let wrappedDetails = [];

        if ((jsonContent.isThereDrilling === "Standard" || jsonContent.isThereDrilling === "Custom") && drillDetails) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          wrappedDetails = doc.splitTextToSize(drillDetails, colInnerWidth);
          detailsDims = doc.getTextDimensions(wrappedDetails);
        }

        // Total content height
        const totalTextHeight =
          statementDims.h +
          (wrappedDetails.length ? detailsDims.h + 2 : 0);

        // Vertical center calculation
        let currentY =
          rightBtnTop + (drillBoxH - totalTextHeight) / 2 + 4;

        // Draw statement
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text(wrappedStatement, rightColX + 3, currentY);

        // Draw details
        if (wrappedDetails.length) {
          currentY += statementDims.h + 2;

          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          doc.text(wrappedDetails, rightColX + 3, currentY);
        }



        // Custom Drilling Notes Box (Grey Box for full notes display)
        // Custom Drilling Notes Box (Grey Box for full notes display)
        let notesBoxHeight = 0;
        if ((jsonContent.isThereDrilling === "Custom" && jsonContent.drillingtxt) ||
          jsonContent.isThereDrilling === "Standard") {
          const notesBoxX = rightColX - 2;
          const notesBoxY = rightBtnTop + drillBoxH;
          const notesBoxWidth = drillBoxW;

          // Calculate max available height to prevent overflow
          const maxAvailableHeight = 285 - notesBoxY;
          const notesBoxHeight = Math.min(28, maxAvailableHeight);

          // Draw grey box with border
          doc.setFillColor(220, 220, 220);
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.1);
          doc.rect(notesBoxX, notesBoxY, notesBoxWidth, notesBoxHeight, 'FD');

          // Add header
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0);
          doc.text(jsonContent.isThereDrilling === "Custom" ? "Notes for Customization:" : "Standard Drilling Details:",
            notesBoxX + 2, notesBoxY + 4);

          // Add content
          doc.setFont("helvetica", "italic");
          doc.setFontSize(7);
          const maxWidth = notesBoxWidth - 4;

          // Different content for Standard vs Custom
          const contentText = jsonContent.isThereDrilling === "Custom"
            ? jsonContent.drillingtxt
            : "Standard Option can include the following:\n" +
            "Double Hang: No Holes.\n" +
            "Medium Hang: Holes 36\" from the ground up (1 Rod + >1 Shelf)\n" +
            "Long Hang: Holes 18\" from the ground up (1 Rod + 1 Shelf)\n" +
            "Full Shelving Sections: Holes still to be drilled.";

          const wrappedNotes = doc.splitTextToSize(contentText, maxWidth);

          let yPosition = notesBoxY + 9;
          const lineHeight = 3.5;
          wrappedNotes.forEach((line) => {
            if (yPosition < notesBoxY + notesBoxHeight - 1) {
              doc.text(line, notesBoxX + 2, yPosition);
              yPosition += lineHeight;
            }
          });

          doc.setTextColor(0, 0, 0);
          doc.setFontSize(8);
        }
        // bottom of right column content
        const rightBottom = rightBtnTop + drillBoxH +
          ((jsonContent.isThereDrilling === "Custom" && jsonContent.drillingtxt) ||
            jsonContent.isThereDrilling === "Standard" ? notesBoxHeight : 0);
        const outerBottom = Math.max(leftBottom, rightBottom); // bottom padding
        const outerHeight = outerBottom - outerY;

        doc.rect(outerX, outerY, outerWidth, outerHeight);
        // Set light gray for lines
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);

        // Horizontal line after left image (span full inner width)
        doc.line(leftColX + innerPad, leftImgY + imageHeight + 2, leftColX + innerPad + colInnerWidth, leftImgY + imageHeight + 2);

        // Horizontal line after right image (span full inner width)
        doc.line(rightColX + innerPad, rightImgY + imageHeight + 2, rightColX + innerPad + colInnerWidth, rightImgY + imageHeight + 2);

        // Vertical line between two columns (after outerHeight is known)
        const midX = outerX + colWidth + (gapBetweenBoxes / 2);
        doc.line(midX, outerY, midX, outerY + outerHeight);
        doc.line(3, 108, 207, 108);


        doc.setDrawColor(0, 0, 0);
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");


        // Designer Signature (on the left)
        doc.text("Designer Signature: __________________", 5, 290);

        // Client Signature (on the right)
        doc.text("Client Signature: __________________", 87, 290);
        doc.addPage();
      }


      //------------------------ End Backing & Drilling Page -------------------------

      // Render header on first page

      // Page 2
      // ----------------------
      // 4. Terms & Conditions Section (Dynamic Backing Option)
      // ----------------------

      const termsY = 23 + 17 + 30;
      const termsW = 204;
      const termPadding = 4;
      const termBoxX = 3;
      doc.setDrawColor(0, 0, 0);
      doc.setTextColor(0, 0, 0);
      // Render header on first page
      drawHeader(doc, logoBase64, jobID, false);

      // Combined Terms & Conditions text sections
      const terms = [
        `1. Applicability and Acceptance of these Terms

These terms and conditions for services (these "Terms") are the only terms that govern the sale of products and provision of services by 2340993 Ontario Ltd., operating as Dream Spaces of Central Ontario ("The Company", "Us", "We") to the applicable client ("Client", "You").

The accompanying order confirmation (the "Order Confirmation") and these Terms (collectively, this "Agreement") comprise the entire agreement between the parties, and supersede all prior or contemporaneous understandings, agreements, negotiations, representations and warranties, and communications, both written and oral. In the event of any conflict between these Terms and the Order Confirmation, these Terms shall govern.`,

        `2. Products and Services

The company shall provide the products and services to Client as described in the Order Confirmation (the "Products" and "Services") in accordance with these Terms. By accepting this Agreement, you understand that you are purchasing both services and a custom product, including design, administration, drafting, production, installation and warranty.`,

        `3. Order Confirmation

The accompanying Order Confirmation reflects the project specifications that you selected based on discussions with a Company representative (the "Designer"). It is your responsibility to review these specifications prior to signing this Agreement.

The company’s approval of any project specifications submitted pursuant to this Agreement is subject to technical review and management approval.

Production begins on your custom project three (3) business days following your signing of this Agreement, which constitutes your acceptance of the Order Confirmation.

Once you have accepted an Order Confirmation, the company is under no obligation to provide refunds or credits in the event of full project cancellation.`,

        `4. Change Orders

Any request to change the project specifications after an Order Confirmation has been accepted (a "Change Order") must be completed and submitted by the Designer. You will be required to sign off on the change order to confirm acceptance.

If changes are made to a product that has already been ordered or produced, you will be responsible for payment of both the original product and the newly required items.`,

        `5. Product Specifications

The company may make minor adjustments to the section dimensions agreed upon in the Order Confirmation. If these adjustments exceed 5 inches, a company representative will contact you to review and approve a revised Order Confirmation. For changes of 5 inches or less, the company reserves the right to make professional adjustments as needed without further approval.

In line with industry standards, the Product will be drilled with holes at 32mm spacing for adjustments and future versatility. Outside end panels will not be drilled. If your Order Confirmation does not specify areas in which to avoid drilling, all areas will be drilled. Custom drilling will be subject to additional charges.

the company reserves the right to determine whether baseboard removal is necessary to ensure the best outcome for the client. If existing baseboards interfere with the installation, they will be removed as needed.

Ontario Regulation 332/12, the "Building Code", may restrict our ability to install around sprinklers, fire alarms, windows, doors, cold air returns, heating vents, and similar elements. Compliance with these regulations may limit design or placement options.

The company is not responsible for gaps caused by out of level walls, floors, windows, ceilings, or doors. You may choose to purchase molding to help account for these problems, at an additional cost. Additional orders for molding must not delay payment of the final balance.`,

        `6. Price and Payment

In consideration of the sale of the Products and the provision of the Services by the company, you shall pay the fees set forth in the applicable Order Confirmation (the "Price").

You agree to remit full payment of the Price to the installer upon their arrival at the installation site. Work will not begin on your installation until the full final payment is made.

If you are using financing to cover the balance of the Price, the person signing for the Order Confirmation must be the same person signing for the applicable financing agreement.

Should any process of law be required by the company to collect any monies due, you agree to pay all costs, interest, and attorney’s fees incurred by the company.`,

        `7. Scheduling, Attendance and Postponement

Postponing your installation will incur a 10% fee for changes made within 5 days and a 20% fee for changes within 48 hours of your scheduled installation date.

The company will contact you to confirm your installation before the scheduled date. If we are unable to confirm with you via phone or email within 24 hours of the installation, the installation will be postponed and you will be subject to a 20% rescheduling fee.

The company will provide you with an estimated arrival time for your installation team. This time is approximate, and unforeseen circumstances may affect our ability to arrive as scheduled. We will make every effort to arrive on time and will inform you promptly of any delays. If a delay requires rescheduling, the company will make reasonable efforts to accommodate a new installation time that is convenient for you.

You agree to be present at the time of installation to review the completed work with the installer. The company will not accept delayed or withheld payment due to your absence during the final review.`,

        `8. Client’s Obligations – Preparing for Installation

a) Installers require a power outlet to operate outdoor tools near the installation truck. The installer will require a parking space, and their vehicle may not fit in underground parking. It is your responsibility to provide accessible parking near the installation area.

b) Please provide the installation team with access to a bathroom during the installation.

c) The installation area must be completely cleared before our arrival on installation day. The company installers are instructed not to move personal property. If anything must be moved, the company is not liable for damage. A minimum labour fee of $250.00 plus applicable taxes will apply. Labour is charged out at $250.00 per hour, per worker, plus applicable taxes. In extreme cases, installation may be delayed.

d) If you are painting the installation space, we recommend that you remove shelves, rods, cleats, and baseboards prior to patching and painting. Painting must be completed at least 48 hours before installation to ensure dryness.

e) Wall damage and missing texture or paint are common occurrences where previously installed items are removed prior to your installation. The company always recommends backing for all units, but especially where previously installed items are to be removed to make room for your new unit. Backing is available at an additional cost. The company does not take any responsibility for damage as a result of tear out; repair work is the sole responsibility of the client.

f) If the installation site is under construction on the day of installation, this work may affect our ability to perform your installation. To avoid extra charges and delays, contact the main office a minimum of five business days prior to your scheduled installation. The company is not responsible for delays due to subcontractor work.

g) Installation will be postponed if the area is inaccessible or unsafe due to weather, obstructions, or construction. You are responsible for clearing walkways and walkways and ensuring a safe working environment.

h) Our installers will protect your flooring by removing their shoes and using drop cloths. If you would like additional protection, make arrangements before installation day.

i) If the installation site is in a multi-level building with an elevator, you are responsible for booking elevator access for the installers. Failure to secure access may impact installation.

j) You will receive a 3D rendering of your project via email prior to installation to confirm design expectations. If you do not respond within 72 business hours, the company will consider the rendering approved and proceed with production.

k) If your failure to meet any obligations results in delay or rescheduling, a 20% rescheduling fee shall apply.`,

        `9. Clean up and Extra Work

While our installer will take reasonable precautions to avoid excessive mess and will vacuum and clean the area following the installation, it is normal to have a small amount of dust as a result of the work performed.

If extra tear-out is needed beyond the standard removed (one rod, two cleats, one shelf, and baseboard per wall), an additional $250.00 charge will be added to your final bill. Any other extra work not included in this contract will be charged at $250.00 per worker, per hour, plus applicable taxes.

the company does not cut, remove or install flooring.`,

        `10. Safety

For your safety, and the safety of our installers, we request that you not be present in the immediate workspace during installation to avoid any unnecessary risk of injury.

For their safety, please keep pets and children away from installers and the installation area.`,

        `11. Client's Acts or Omissions

If the company's performance of its obligations under these Terms is prevented or delayed by any act or omission by you or your agents, subcontractors, consultants or employees, the company shall not be deemed in breach of its obligations or otherwise liable for any costs, charges or losses sustained or incurred by you to the extent arising directly or indirectly from such prevention or delay.`,

        `12. Warranties

The company provides a Lifetime Warranty for all custom-designed storage units and organizers manufactured and installed by Dream Spaces employees. The company warrants that such Products are fit and safe for use consistent with and will materially conform to the specifications in the Order Confirmation. The warranty covers all units installed in the home or dwelling indicated in the contract. The warranty provides for the replacement of faulty components manufactured by the company under normal wear and use from the date of installation. The warranty also covers any issues resulting from improper installation by our installers.

The company does not provide any warranty for products that (a) have been damaged due to abuse, misuse, fire, flood, earthquake, acts of nature, failure caused by corrosion, and/or storage of over-weighted objects; or (b) have been reconstructed, repaired, modified or altered by anyone other than the company.

The company provides a minimum one-year warranty for all third-party products and components not manufactured by the company, but which were installed by the company. This warranty is established by the product manufacturer and is not governed or determined by the company. This warranty is subject to the same limitations and exclusions as the Lifetime Warranty.`,

        `13. DISCLAIMER OF OTHER WARRANTIES; NON-RELIANCE

EXCEPT FOR THE EXPRESS WARRANTIES SET FORTH IN SECTION 12, (A) NEITHER the company NOR ANY PERSON ON the company’S BEHALF HAS MADE OR MAKES ANY EXPRESS OR IMPLIED REPRESENTATION OR WARRANTY WHATSOEVER, EITHER ORAL OR WRITTEN, INCLUDING ANY WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, OR NON-INFRINGEMENT, WHETHER ARISING BY LAW, COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE OF TRADE OR OTHERWISE, ALL OF WHICH ARE EXPRESSLY DISCLAIMED; AND (B) THE CLIENT ACKNOWLEDGES THAT IT HAS NOT RELIED UPON ANY REPRESENTATION OR WARRANTY MADE BY the company, OR ANY OTHER PERSON ON the company’S BEHALF, EXCEPT AS SPECIFICALLY PROVIDED IN THIS SECTION.`,

        `14. LIMITATION OF LIABILITY

a) LIMITATION. IN NO EVENT SHALL the company OR ITS REPRESENTATIVES BE LIABLE FOR ANY CONSEQUENTIAL, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE OR ENHANCED DAMAGES, LOST PROFITS OR REVENUES OR DIMINUTION IN VALUE, ARISING OUT OF OR RELATING TO ANY BREACH OF THIS AGREEMENT, REGARDLESS OF WHETHER SUCH DAMAGES WERE FORESEEABLE, WHETHER OR NOT THE CLIENT WAS ADVISED OF THE POSSIBILITY OF SUCH DAMAGES AND THE LEGAL OR EQUITABLE THEORY (CONTRACT, TORT OR OTHERWISE) UPON WHICH THE CLAIM IS BASED, AND NOTWITHSTANDING THE FAILURE OF ANY AGREED OR OTHER REMEDY OF ITS ESSENTIAL PURPOSE.

b) MAXIMUM LIABILITY FOR DAMAGES. IN NO EVENT SHALL the company’S AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT, WHETHER ARISING OUT OF OR RELATED TO BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE, EXCEED THE TOTAL OF THE AMOUNTS PAID TO the company PURSUANT TO THE APPLICABLE ORDER CONFIRMATION GIVING RISE TO THE CLAIM.

c) ASSUMPTION OF RISK. WITHOUT LIMITING THE GENERALITY OF THE FOREGOING, THE CLIENT ASSUMES ALL RISK AND LIABILITY FOR THE RESULTS OBTAINED BY THE USE OF ANY PRODUCTS IN THE PRACTICE OF ANY PROCESS, WHETHER IN TERMS OF OPERATING COSTS, GENERAL EFFECTIVENESS, SUCCESS OR FAILURE RELATED TO THE USE OF SUCH PRODUCTS.`,

        `15. Severability

If any term or provision of this Agreement is invalid, illegal or unenforceable in any jurisdiction, such invalidity, illegality or unenforceability does not affect any other term or provision of this Agreement or invalidate or render unenforceable such term or provision in any other jurisdiction. Upon a determination that any term or provision is invalid, illegal or unenforceable, the parties shall negotiate in good faith to modify this Agreement to effect the original intent of the parties as closely as possible in order that the transactions contemplated hereby be consummated as originally contemplated to the greatest extent possible.`,

        `16. Waiver

No waiver under this Agreement is effective unless it is in writing and signed by an authorized representative of the company. Any waiver authorized on one occasion is effective only in that instance and only for the purpose stated, and does not operate as a waiver on any future occasion. None of the following constitutes a waiver or estoppel of any right, remedy, power, privilege or condition arising from this Agreement: (a) any failure or delay in exercising any right, remedy, power or privilege or in enforcing any condition under this Agreement; or (b) any act, omission or course of dealing between the parties.`,

        `17. Governing Law

This Agreement is made under and will be construed in accordance with the laws of the Province of Ontario. Any suit to enforce any provision of this Agreement, or arising out of or based upon this Agreement, shall be brought exclusively in the courts of the Province of Ontario.`,
      ];

      let currentY = termsY + 10;
      const lineHeight = 3.2;
      const boxSpacing = 1;
      const pageHeight = doc.internal.pageSize.height;
      const bottomMargin = 10;
      const startY = termsY + 10;
      let firstTermsPage = null;

      //const termsStartPage = doc.getNumberOfPages() + 1;
      terms.forEach((term) => {
        const pageWidth1 = doc.internal.pageSize.getWidth();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(
          "GENERAL TERMS AND CONDITIONS",
          pageWidth1 / 2, // x-coordinate: center of page
          11, // y-coordinate: e.g. 15mm from top
          { align: "center" } // center alignment
        );
        if (firstTermsPage === null) {
          firstTermsPage = doc.internal.getCurrentPageInfo().pageNumber;
        }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);

        // Split into header and body sections
        const [rawHeader, ...rawBodySections] = term.split("\n\n");
        const headerLines = doc.splitTextToSize(
          rawHeader,
          termsW - termPadding * 2
        );
        const bodyText = rawBodySections.join("\n\n");
        const bodyLines = doc.splitTextToSize(
          bodyText,
          termsW - termPadding * 2
        );

        // Calculate box height based on total lines
        const totalLines = headerLines.length + bodyLines.length;
        const boxHeight = totalLines * lineHeight + termPadding * 1.6;

        // Add new page and header if box would overflow
        if (currentY + boxHeight > pageHeight - bottomMargin) {
          doc.addPage();
          drawHeader(doc, logoBase64, jobID, false);
          currentY = startY;
        }

        // Draw the box
        doc.rect(termBoxX, currentY - termPadding, termsW, boxHeight);

        // Draw header in bold
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text(headerLines, termBoxX + termPadding, currentY);

        // Draw body with subpoint labels in bold
        let bodyY = currentY + headerLines.length * lineHeight;
        bodyLines.forEach((line) => {
          const match = line.match(/^([a-z]\))\s*(.*)/i);
          if (match) {
            const label = match[1] + " ";
            const rest = match[2];
            // Draw label bold
            doc.setFont("helvetica", "bold");
            doc.text(label, termBoxX + termPadding, bodyY);
            // Draw rest normal
            const labelWidth = doc.getTextWidth(label);
            doc.setFont("helvetica", "normal");
            doc.text(rest, termBoxX + termPadding + labelWidth, bodyY);
          } else {
            doc.setFont("helvetica", "normal");
            doc.text(line, termBoxX + termPadding, bodyY);
          }
          bodyY += lineHeight;
        });

        // Move currentY for next term
        currentY += boxHeight + boxSpacing;
      });
      const lastTermsPage = doc.getNumberOfPages();

      doc.setPage(lastTermsPage); // move to second last page

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);

      // Designer Signature
      doc.text("Designer Signature: __________________", 5, 290);

      // Client Signature
      doc.text("Client Signature: __________________", 87, 290);

      const sourcepdf = doc.output("datauristring");

      let sourceBytes = base64ToUint8Array(
        sourcepdf.replace(
          `data:application/pdf;filename=generated.pdf;base64,`,
          ""
        )
      );
      let sourcePdf = await PDFDocument.load(sourceBytes);
      let allInsertions = [];
      const pdfURLs = [];

      if (
        typeof designFileURL === "string" &&
        typeof pricePresentationFileURL === "string" &&
        designFileURL.trim() !== "" &&
        pricePresentationFileURL.trim() !== ""
      ) {
        pdfURLs.push({ [jobID]: [designFileURL, pricePresentationFileURL] });
      } else if (
        typeof designFileURL === "string" &&
        designFileURL.trim() !== ""
      ) {
        pdfURLs.push({ [jobID]: [designFileURL] });
      } else if (
        typeof pricePresentationFileURL === "string" &&
        pricePresentationFileURL.trim() !== ""
      ) {
        pdfURLs.push({ [jobID]: [pricePresentationFileURL] });
      }
      for (const item of pdfURLs) {
        const jobId = Object.keys(item)[0];
        const urls = item[jobId];
        const baseIndex = parseInt(jobId);

        for (const url of urls) {
          allInsertions.push({ insertAt: baseIndex, url });
        }
      }
      // Step 2: Sort by insertAt index
      allInsertions.sort((a, b) => a.insertAt - b.insertAt);

      // Step 3: Insert sequentially, adjusting index as pages are added

      // Assume: sourcePdf already loaded, and allInsertions is built & sorted by insertAt
      // Each item: { insertAt: number, url: string }

      // 1) Group URLs by insertAt (baseIndex)
      const groups = new Map();
      for (const { insertAt, url } of allInsertions) {
        if (!groups.has(insertAt)) groups.set(insertAt, []);
        groups.get(insertAt).push(url);
      }

      // 2) Iterate groups in ascending insertAt
      // In the design-sheet/price-presentation insertion logic:
      let offset = closetIndices.length + pdfPageCount + 1; // was resultArray.length
      let firstGroup = true;
      const excludedPages = [];

      for (const [insertAt, urls] of [...groups.entries()].sort(
        (a, b) => a[0] - b[0]
      )) {
        // Add +1 for every distinct baseIndex after the first
        if (!firstGroup) offset += 1;
        firstGroup = false;

        // Track how many pages we add within this group, so they land contiguously
        let groupAdded = 0;

        // 3) Process each URL in the group (in order)
        for (const url of urls) {
          const response = await fetch(url);
          const blob = await response.blob();
          const bytes = await blobToUint8Array(blob);

          const extPdf = await PDFDocument.load(bytes);
          const extPages = await sourcePdf.copyPages(
            extPdf,
            extPdf.getPageIndices()
          );

          // 4) Insert each page, standardized to A4, preserving aspect ratio & centering
          for (let i = 0; i < extPages.length; i++) {
            const page = extPages[i];

            // Compute live target index for this page
            let targetIndex = offset + groupAdded + i;

            // Clamp to current page count to avoid OOB inserts
            const pageCount = sourcePdf.getPageCount();
            if (targetIndex < 0) targetIndex = 0;
            if (targetIndex > pageCount) targetIndex = pageCount;

            // A4 sizes in points
            const a4Width = 595.28;
            const a4Height = 841.89;

            const originalWidth = page.getWidth();
            const originalHeight = page.getHeight();

            // Scale to fit while preserving aspect ratio
            const scaleX = a4Width / originalWidth;
            const scaleY = a4Height / originalHeight;
            const scale = Math.min(scaleX, scaleY);

            const scaledWidth = originalWidth * scale;
            const scaledHeight = originalHeight * scale;

            // Center on page
            const xOffset = (a4Width - scaledWidth) / 2;
            const yOffset = (a4Height - scaledHeight) / 2;

            // Insert blank A4 page then draw the copied page onto it
            const newPage = sourcePdf.insertPage(targetIndex, [
              a4Width,
              a4Height,
            ]);
            const embedded = await sourcePdf.embedPage(page);
            newPage.drawPage(embedded, {
              x: xOffset,
              y: yOffset,
              xScale: scale,
              yScale: scale,
            });
            excludedPages.push(targetIndex + 1);
          }

          // Count pages added from this URL within the group
          groupAdded += extPages.length;
        }

        // Step 2: Sort by insertAt index
        allInsertions.sort((a, b) => a.insertAt - b.insertAt);

        // Step 3: Insert sequentially, adjusting index as pages are added

        // Assume: sourcePdf already loaded, and allInsertions is built & sorted by insertAt
        // Each item: { insertAt: number, url: string }

        // After finishing the whole group, advance the global offset by how many pages the group added
        offset += groupAdded;

        logger.debug(`Group @ baseIndex=${insertAt}: added ${groupAdded} pages; offset now ${offset}`);
        logger.debug("Newly added page numbers (1-based):", excludedPages);
      }

      // const insertBytes = base64ToUint8Array(insertPDF);

      // const insertPdf = await PDFDocument.load(insertBytes);

      const mergedBytes = await sourcePdf.save();

      const newBlob = new Blob([mergedBytes], { type: "application/pdf" });
      logger.debug("PDF Blob size:", { bytes: newBlob.size, mb: (newBlob.size / (1024 * 1024)).toFixed(2) });
      const blobUrl = URL.createObjectURL(newBlob);

      if (isSavePreviewOnly) {
        // ── submission.completed (already signed) ──
        if (pdfEventType === "submission.completed") {
          const wantsResign = window.confirm(
            "The Contract for this job has already been signed. Click OK if you want to generate a new one or Cancel to view the signed contract."
          );

          if (wantsResign) {
            const newWindow = window.open();
            if (newWindow) {
              const html = `
          <html>
            <head><title>PDF Preview</title></head>
            <body style="margin:0; padding:0; overflow:hidden;">
              <iframe src="${blobUrl}" type="application/pdf" width="100%" height="100%" style="border:none;"></iframe>
            </body>
          </html>
        `;
              newWindow.document.open();
              newWindow.document.write(html);
              newWindow.document.close();
            } else {
              const downloadLink = document.createElement("a");
              downloadLink.href = blobUrl;
              downloadLink.download = "preview.pdf";
              const userConfirmed = window.confirm(
                "Your browser blocked the PDF preview pop-up.\n\nWould you like to download the PDF instead?"
              );
              if (userConfirmed) downloadLink.click();
            }
            setTimeout(() => setHasSeenPreview(true), 3000);
            setHasChangesAfterSend(true);   // ← mark changes exist
            setShowLoading("none");
            return;
          } else {
            const win = window.open(pdfURL, "_blank");
            if (!win) {
              notify("Popup blocked. Please allow popups to view the PDF.", "warning");
            }
            setShowLoading("none");
            return;
          }
        }

        // ── submission.created (pending signature) ──
        if (pdfEventType === "submission.created") {
          const wantsUpdate = window.confirm(
            "The contract has been sent out for signature. Do you still want to send it again?."
          );

          if (wantsUpdate) {
            const newWindow = window.open();
            if (newWindow) {
              const html = `
          <html>
            <head><title>PDF Preview</title></head>
            <body style="margin:0; padding:0; overflow:hidden;">
              <iframe src="${blobUrl}" type="application/pdf" width="100%" height="100%" style="border:none;"></iframe>
            </body>
          </html>
        `;
              newWindow.document.open();
              newWindow.document.write(html);
              newWindow.document.close();
            } else {
              const downloadLink = document.createElement("a");
              downloadLink.href = blobUrl;
              downloadLink.download = "preview.pdf";
              const userConfirmed = window.confirm(
                "Your browser blocked the PDF preview pop-up.\n\nWould you like to download the PDF instead?"
              );
              if (userConfirmed) downloadLink.click();
            }
            setTimeout(() => setHasSeenPreview(true), 3000);
            setHasChangesAfterSend(true);   // ← mark changes exist
            setShowLoading("none");
            return;
          } else {
            //alert("The previous contract is still pending signature. Please check your inbox.");
            setShowLoading("none");
            return;
          }
        }

        // ── Normal preview (no prior signing) ──
        const newWindow = window.open();
        if (newWindow) {
          const html = `
      <html>
        <head><title>PDF Preview</title></head>
        <body style="margin:0; padding:0; overflow:hidden;">
          <iframe src="${blobUrl}" type="application/pdf" width="100%" height="100%" style="border:none;"></iframe>
        </body>
      </html>
    `;
          newWindow.document.open();
          newWindow.document.write(html);
          newWindow.document.close();
        } else {
          const downloadLink = document.createElement("a");
          downloadLink.href = blobUrl;
          downloadLink.download = "preview.pdf";
          const userConfirmed = window.confirm(
            "Your browser blocked the PDF preview pop-up.\n\nWould you like to download the PDF instead?"
          );
          if (userConfirmed) downloadLink.click();
        }
        setTimeout(async () => {
          setHasSeenPreview(true);
        }, 3000);
        setShowLoading("none"); // Show "Send for sign" + "Hide PDF"
        return;
      }
      const des = designerName;
      const fileName =
        jobID + "" + des + "" + moment().format("YYYYMMDDTHH:mm:ss") + ".pdf";
      //const base64 = doc.output("dataurl");
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      // Convert blob to base64
      const arrayBuffer = await blob.arrayBuffer();
      //const pdfBuffer = doc.output("arraybuffer");
      const base64Str = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const totalPages = pdfDoc.getPageCount();
      logger.debug("Total PDF pages:", totalPages);
      logger.debug("Before excluded pages:", excludedPages);

      excludedPages.push(
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
      );
      logger.debug("After excluded pages:", excludedPages);
      // Generate PDF Blob URL
      if (!isSavePreviewOnly) {
        // The payment/letter/signature content may have overflowed onto an
        // extra page (see letterPageBroke above) — use the actual captured
        // page number(s) rather than re-deriving them, so the signature
        // area still lands on whichever page the letter content is on.
        const signaturePagePayment = letterPageBroke
          ? paymentPageNumber + 1
          : paymentPageNumber;

        // build two flat arrays of { page, coords } objects
        const designerAreas = [];
        const clientAreas = [];

        //if (window.backingPages && window.backingPages.size > 0) {
        //  for (const pageNum of window.backingPages) {
        //    clientAreas.push({
        //      page: pageNum,
        //      x: 176,  y: 450,
        //      w: 100,  h: 30
        //    });
        //  }
        //}

        for (let page = 1; page <= totalPages; page++) {
          if (!excludedPages.includes(page)) {
            if (page === signaturePagePayment) {
              // Payment page — signature is at y=229mm in jsPDF
              designerAreas.push({ page, x: 100, y: 695, w: 140, h: 40 });
              clientAreas.push({ page, x: 315, y: 695, w: 140, h: 40 });
            } else {
              // All other signed pages — signature is at y=290mm in jsPDF
              designerAreas.push({ page, x: 100, y: 795, w: 140, h: 40 });
              clientAreas.push({ page, x: 315, y: 795, w: 140, h: 40 });
            }
          }
        }


        // helper to map an area into a distinct field
        function makeFieldsForRole(role, areas) {
          return areas.map((area, idx) => ({
            role,
            name: `${role.toLowerCase()}_signature_page_${area.page}_${idx}`,
            type: "signature",
            required: true, // ← enforce this specific signature
            areas: [
              {
                // only one area per field
                page: area.page.toString(),
                x: area.x.toString(),
                y: area.y.toString(),
                w: area.w.toString(),
                h: area.h.toString(),
              },
            ],
          }));
        }

        const documents = {
          documents: [
            {
              name: fileName,
              file: base64Str,
              fields: [
                // one field per designer-area:
                ...makeFieldsForRole("Designer", designerAreas),
                // one field per client-area:
                ...makeFieldsForRole("Client", clientAreas),
              ],
            },
          ],
          name: fileName,
          folder_name: "closets",
          id: templateId,
        };

        //alert(templateId);
        const templateResponse =
          await GeneratePDFService.UploadTemplateToDocuSeal(documents);
        const designer = await GeneratePDFService.fetchDesigner(jobID);
        const signRequest = {
          template_id: templateResponse.id,
          send_email: true,
          order: "preserved",
          submitters: [
            {
              role: "Designer",
              email: designer?.email ?? LocalStorageService.getEmail(),
              send_email: true,
            },
            {
              role: "Client",
              email: clientemail,
              send_email: true,
            },
          ],
        };

        setShowLoading("none");
        const signResponse =
          await GeneratePDFService.SendRequestForSignDocument(
            signRequest,
            jobID
          );
        if (signResponse) notify("Sent sign request successfully!", "success");
        logger.debug("Sign request response:", signResponse);
      }

    } catch (err) {
      logger.error("PDF submit failed:", err);
      notify("Something went wrong while generating the PDF.", "error");
      setShowLoading("none");
    } finally {
      setShowLoading("none");
    }
  };


  return { handleSubmit };
}